/** @module csi/table/Widget */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/_base/window",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/dom-style",
	"dojo/has",
	"dojo/keys",
	"dojo/promise/all",
	"dojo/query",
	"dojo/topic",
	"dojo/when",
	"dijit/registry",
	"src/c8-platform/type/customView",
	"csi",
	"csi/_base/Deferred",
	"csi/_base/DeferredTracking",
	"csi/_base/underscore",
	"csi/action",
	"csi/action/Controller",
	"csi/action/FormData",
	"csi/api",
	"csi/constants",
	"csi/core/ExtendedPath",
	"csi/core/Key",
	"csi/core/SpecBuilder",
	"csi/def",
	"csi/def/Action",
	"csi/def/Heading",
	"csi/gallery/_base",
	"csi/heading/_base",
	"csi/heading/Spec",
	"csi/heading/Specs",
	"csi/html",
	"csi/image/_base",
	"csi/locale",
	"csi/middleware/common",
	"csi/middleware/attributes/attributeKeysService",
	"csi/middleware/customView/customViewDataManagementService",
	"csi/middleware/customView/options/TableCustomViewOptionsStore",
	"csi/middleware/customView/options/TableOptionsTab",
	"csi/model/image",
	"csi/prefs/_base",
	"csi/reflection",
	"csi/service/imageService",
	"csi/service/viewService",
	"csi/service/writerService",
	"csi/state",
	"csi/state/rootStore",
	"csi/store/GroupNodes",
	"csi/store/PageSizeWithoutAll",
	"csi/table",
	"csi/table/_SelectionMixin",
	"csi/table/SlimPreferences",
	"csi/table/TableComparisonData",
	"csi/table/TablePlaceholder",
	"csi/util",
	"csi/utility/arrayUtility",
	"csi/view/_CustomViewMixin",
	"csi/view/_CustomViewQueryMixin",
	"csi/view/_QueryMixin",
	"csi/view/_query/DatabaseReport",
	"csi/view/_query/RollupReport",
	"csi/view/Widget",
	"csi/widget/ActionsComboButton",
	"csi/widget/Automation",
	"csi/widget/SortIndicator",
	"csi/widget/Tooltip",
	"csi/xml",
	"csi/xml/Query",
	"csi/xml/QueryProcessor",
	"csi/xml/Writer",
	"./_CopyPasteMixin",
	"./_DndTableMixin",
	"./MemoryTable",
	"./Preferences",
	"./ScrollDataFixed",
	"./TableComparisonData",
	"./ToolbarBatchFilter",
	"./Tooltip",
	"dojo/text!csi/templates/table.html",

	"csi/action/ApplySort",
	"csi/action/Excel",
	"csi/action/ExcelSwitchboard",
	"csi/action/RemoveSort",
	"csi/table/matrix/Path",
	"csi/view/ToolbarPluginSuspend",
	"csi/widget/InvalidMessage",
	"csi/widget/QueryMessage",
	"./QuickSearch",
	"./ToolbarCollapse",
	"./ToolbarColumns",
	"./ToolbarExcelFlat",
	"./ToolbarExpand",
	"./ToolbarFilter",
	"./ToolbarPluginChart",
	"./ToolbarQuickSearch",
], function(declare, lang, win, domClass, domConstruct, domStyle, has, keys, all, query, topic, when, registry, {CustomViewVersion},
	csi, Deferred, Tracking, _, csiAction, Controller, FormData, api, constants, ExtendedPath, Key, SpecBuilder, def, Action, Heading, csiGallery, csiHeading,
	Spec, Specs, html, csiImage, locale, {convertEnumToComponentItem},
	{CustomViewAttributeMap, getFirstAttributeKey, migrateTableKeysV791}, {getNumGroupingLevels},
	TableCustomViewOptionsStore, TableOptionsTab, {IMAGE_SIZE_CUSTOM, getImageSizeBySize}, csiPrefs,
	reflection, {waitForImagesToLoad}, {collectSpecDependencyPaths, generateColumnSpecsFromAttributeSpecs, generateSpecsForLevel},
	{registerWriterCallback}, state, {browserStore, rootStore},
	GroupNodes, PageSizeWithoutAll, csiTable, _SelectionMixin, SlimPreferences, {createCompareData}, TablePlaceholder, util,
	{arrayLast}, _CustomViewMixin, _CustomViewQueryMixin, _QueryMixin, DatabaseReport, RollupReport, ViewWidget, ActionsComboButton,
	Automation, SortIndicator, Tooltip, xml, Query, QueryProcessor, Writer, _CopyPasteMixin, _DndTableMixin, MemoryTable, Preferences, ScrollDataFixed, TableComparisonData, ToolbarBatchFilter, TableTooltip, template) {

	html.registerDataAttribute("TABLE_CELL_COMPARE_KEY", "table-compare");
	html.registerDataAttribute("DATA_TABLE_STATE", "table-state");
	html.registerDataAttribute("DATA_TABLE_ROW_URL", "table-row");

	csiAction.registerControllers("Table", {
		Aggregate: "csi/table/Aggregate",
		AggregateFrom: "csi/table/AggregateFrom",
		Copy: "csi/table/Copy",
		CustomViewManage: "csi/table/TableCustomViewController",
		CustomViewSaveAs: "csi/table/TableCustomViewController",
		Delete: "csi/table/Delete",
		Edit: "csi/table/Edit",
		Insert: "csi/table/Insert",
		Move: "csi/table/Move",
		MoveDown: "csi/table/MoveUpDown",
		MoveUp: "csi/table/MoveUpDown",
		MoveFrom: "csi/table/MoveFrom",
		New: "csi/table/New",
		NewFrom: "csi/table/NewFrom",
		Popup: "csi/table/Popup",
		TableApplySort: "csi/table/ApplySort",
		TableCollapse: "csi/table/Collapse",
		TableExcel: "csi/table/Excel",
		TableExcelFlat: "csi/table/Excel",
		TableExcelSwitchboard: "csi/table/ExcelForSwitchboard",
		TableExpand: "csi/table/Expand",
		TableHeader: "csi/table/Header",
		TableHierarchy: "csi/table/Hierarchy",
		TableRemoveSort: "csi/table/RemoveSort",
		TableSuspend: "csi/table/Suspend",
		Uncheck: "csi/table/Check",
		ViewPdf: "csi/table/PdfController",
		ViewPageSize: "csi/table/QuerySizeController",
		ViewPrint: "csi/table/PrintController",
		ViewQueryAll: "csi/table/QueryViewAllController",
		ViewQueryMore: "csi/table/QueryMoreController"
	});

	const ACTION_COLUMN_RESIZE = "TableColumnResize";
	const ACTION_HIERARCHY = "TableHierarchy";
	const ACTION_HEADER = "TableHeader";
	const ACTION_NEW_REFRESH = "NewlyCreatedViewRefresh";
	const ACTION_RESET_COLUMN_WIDTHS = "TableResetColumnWidths";
	const ACTION_SELECT_ALL = "TableSelectAll";
	const CSS_FIRST_COLUMN = "firstColumn";
	const CSS_HEADER_ARROW = "csi-table-header-arrow";
	const CSS_MATRIX_COLUMN_FIRST = "csi-table-matrix-column-first";
	const CSS_MATRIX_COLUMN_LAST = "csi-table-matrix-column-last";
	const CSS_MATRIX_COLUMN = "csi-table-matrix-column";
	const CSS_ROW_HIDDEN = "csi-table-hide-row";
	const CSS_GROUP = "tableViewGroup";
	const CSS_REPORT = "tableViewReport";
	const CSS_REPORT_MARKER = "outdated";
	const CSS_ROW = "csi-table-row";
	const GROUP_HEADING_KEY = "__TableGroupHeader__";
	const INCREMENTAL_QUERY_SIZE = 25;
	const NORESULT = "NoResult";
	const REPORT_URL_SUFFIX = "Report";
	const TABLE_MIN_WIDTH = 600;

	csiTable.registerCellClass(CSS_FIRST_COLUMN);
	csiTable.registerCellClass(CSS_MATRIX_COLUMN);
	csiTable.registerCellClass(CSS_MATRIX_COLUMN_FIRST);
	csiTable.registerCellClass(CSS_MATRIX_COLUMN_LAST);

	// Heading: heading for read-only name used for group and report rows
	var _nameHeading = lang.mixin(new Heading(), {
		m_id: xml.ATTR_NAME,
		m_right: xml.RIGHT_NONE
	});

	function _addMatrixCss(spec, bFirst, bLast) {
		const css = [];

		if (bFirst) {
			spec.firstInMatrix = true;
			css.push(CSS_MATRIX_COLUMN_FIRST);
		}

		if (bLast) {
			css.push(CSS_MATRIX_COLUMN_LAST);
		}

		if (!bFirst && !bLast) {
			css.push(CSS_MATRIX_COLUMN);
		}

		spec.valueParams = spec.valueParams || {};
		spec.valueParams.className = [spec.valueParams.className || "", ...css].join(" ");
		spec.headerParams.className = [spec.headerParams.className || "", ...css].join(" ");
	}

	function _addTableCssForGroupSpecs(specs) {
		const nSpecs = specs.length - 1;

		specs.forEach((spec, i) => {
			// add necessary CSS to matrix specs
			const {subs} = spec;
			const nSubs = subs?.length || 0;

			if (nSubs > 1) {
				_addMatrixCss(spec, true, true);

				subs.forEach((specSub, j) => {
					_addMatrixCss(specSub, j == 0, j == nSubs);
				});
			} else if (nSubs === 1) {
				_addMatrixCss(spec, i == 0, i == nSpecs);
				_addMatrixCss(subs[0], i == 0, i == nSpecs);
			} else {
				_addMatrixCss(spec, i == 0, i == nSpecs);
			}
		});
	}

	function _getActionSpec() {
		const css = [csiTable.CSS_ACTION_COLUMN, html.CSS_NO_EXPORT].join(" ");

		return new Spec({
			headerParams: {
				className: css,
				innerText: locale.resource("Actions")
			},
			valueParams: {
				className: css
			}
		});
	}

	function _getExtraSpec() {
		const css = html.CSS_NO_EXPORT + " csi-table-extra-column";

		return new Spec({
			// default width for column is 0, until we know there is leftover space
			getColumnWidth: () => 0,

			// need to differentiate this from action spec, which for now is only spec without a heading
			heading: _.extend(new Heading(), {
				m_class: css
			}),

			// assign key with custom name so it does not have empty string
			key: new Key({
				name: "__TableExtraColumn__"
			}),

			// additional class needed for body cells
			valueParams: {
				className: css + " csi-bg-not-applicable"
			},
		});
	}

	/**
	 * Return report settings that exist for attributes visible in the view
	 * @param {Object[]} reportSettings
	 * @param {ICustomViewAttribute[]} [hiddenKeys]
	 * @return {Object[]}
	 */
	function _getVisibleReportSettings(reportSettings, hiddenKeys) {
		// for now, if an attribute being reported on appears twice in a view, we will report on all columns where it appears;
		// but if it is hidden in any one place, that will remove reporting from all places it appears (even if one is still shown)
		const hiddenMap = new CustomViewAttributeMap(hiddenKeys, true, true);
		return reportSettings?.filter((obj) => !hiddenMap.has(obj.key));
	}

	// reference: https://stackoverflow.com/questions/1382107/whats-a-good-way-to-extend-error-in-javascript
	function QueryLimitError(msg) {
		this.__proto__.__proto__ = Error.apply(null, arguments);
	}

	/**
	 * Hide the DOM element while reserving space by using "visibility:collapse"
	 * @param {DOMNode} domNode
	 */
	function _hide(domNode) {
		$(domNode).addClass(CSS_ROW_HIDDEN);
	}

	/**
	 * Make visible by removing CSS added by _hide above
	 * @param {DOMNode} domNode
	 */
	function _show(domNode) {
		$(domNode).removeClass(CSS_ROW_HIDDEN);
	}

	const _ScrollDataStub = declare(null, {
		fixTableHeights: _.noop,
		fixTableRows: _.noop,
		getFixedColumnWidth: () => 0,
		getAvailableColumnWidth: () => 0,
		isFrozen: () => false,
		onKey: _.noop,
		onKeyRelease: _.noop,
		refreshHeader: _.noop,
		refreshRow: _.noop,
		refreshRows: _.noop,
		resize: _.noop,
		scrollHorizontalToCell: _.noop,
		uninitialize: _.noop,
		updateBodyScroll: _.noop,
		updatePlaceholder: _.noop,
	});

	return declare([ViewWidget, _CustomViewMixin, _QueryMixin, _CustomViewQueryMixin, _CopyPasteMixin, _SelectionMixin, _DndTableMixin], {
		// base widget members
		bDisableContentActions: true,
		bResizeOnChange: true,
		customViewOptionsComponent: TableOptionsTab,
		customViewOptionsStore: TableCustomViewOptionsStore,
		insertCustomViewPluginsAfter: "tableCollapse",
		plugins: ['fullScreen', 'print', {name: 'Export', showLabel: false, icon: "csiActionIcon iconExport", children: ['tableExcel', 'tableExcelFlat', 'tableExcelSwitchboard', 'pdf']},
			'tableExpand', 'tableCollapse', 'columns', 'charts', 'tableRemoveSort', 'tableApplySort', 'refresh', '|', 'pageSize', '|', 'tableSuspend', 'filter', 'quickSearch'],
		pluginsRight: ['fullScreen', 'print', 'Export'],
		pluginClasses: {
			filter: ToolbarBatchFilter,
		},

		prefClass: Preferences,
		querySizeStore: PageSizeWithoutAll,
		templateString: template,
		tooltipClass: TableTooltip,

		// connection points
		chartNode: null,
		tableContainerNode: null,
		tableScrollNode: null,
		headNode: null,
		footNode: null,
		footContentNode: null,
		messageNode: null,
		messageRightNode: null,

		/** @property {boolean} bAlignToolbarWidth - if true, toolbar width in popups will always equal to table width */
		//bAlignToolbarWidth: false,

		/** @property {boolean} bDisableNewActionIfNoGroups - set to true to disable new actions if table is grouped and there are no results */
		//bDisableNewActionIfNoGroups: false,

		/** @property {boolean} bDisableMoveActionIfNoGroups - set to true to disable DnD actions if table is grouped but here are no groups and just newly added line(s) */
		//bDisableMoveActionIfNoGroups

		/** @property {boolean} bForceNewActionsInTable - set to true to force new actions in table */
		//bForceNewActionsInTable: false,

		/** @property {boolean} allowComparison - indicates if comparison is allowed on this view */
		//allowComparison: false,

		/** @property {boolean} allowDeselect - set to false to disable toggling row selection */
		allowDeselect: true,

		// Boolean: allowFullHeight - allow full-height, non-scrolling tables
		allowFullHeight: true,

		/** @property {boolean} allowSuspendInCustomViewPreview - flag to allow suspend in custom view preview */
		// allowSuspendInCustomViewPreview: true,

		// Boolean: allow multi selection of rows on a table
		//allowMultiSelect: false,

		// Boolean: indicates if table height is controlled automatically and not derived from preference tableHeight;
		// if explicitly set to null, then table scrolling is disabled for view
		//allowAutomaticHeight: undefined,

		// Boolean: indicates whether table has multiple levels
		//bHierarchy: false,

		// Boolean: indicates whether to support the image size from custom view in matrix
		//bPrefImageSizeInMatrix: false,

		// preserve number of results already loaded - avoid re-doing "more" after refreshDataRows()
		// bPreserveQueryDisplayCount: false,

		// @property {number} _queryDisplayCountTargetSaved - used to save query display count
		// _queryDisplayCountTargetSaved: 0,

		/** @property {boolean} bRemoveGroupsEmptyTable - if table has group but has no results, we remove groups and table behaves like if it were non grouped
		 * This is our default regime. However, this is conflicting with views, which implements search from toolbar. For these views (FilmStrip/MyTasks) you may
		 * want to set this property to false, otherwise view search with empty results will stuck the table in "no-results" mode */
		bRemoveGroupsEmptyTable: true,

		/** @property {boolean} _bSafariFixApplied - internal flag used to force one time execution of safari styling bug fix - see the code for more info */
		//_bSafariFixApplied: false,

		/** @property {boolean} bShowActionColumn - if false, then the Action column will be hidden  */
		bShowActionColumn: true,

		// Boolean: indicates if view contains report row
		//bReporting: false,

		// Boolean: indicates if view can be expanded during load (be careful not to enable on expensive views)
		//bSupportsExpandLevelOption: false,

		/** @property {DOMNode} bodyTableNode - table element containing table body (same as headTableNode when using dynamic column widths) */
		bodyTableNode: null,

		/** @property {TableComparisonData} compareData - compare data for BOM Compare */
		//compareData: null,

		/** @property {string[]} compareOptions - properties specific to this view to apply to comparison view to accurately reflect state of this view */
		//compareOptions: null,

		// Boolean: indicates whether charts should be hidden
		chartHide: false,

		// Boolean: indicate if table should be hidden when chart is displayed
		chartHideTable: false,

		// Object[]: chart configuration for current view
		//chartSettings: null,

		// Specs: column specification for table based on current custom view and dynamic column queries
		columnData: null,

		// Spec: heading used to render row to allow editing attribute of column matrix nodes
		//columnNodeSpec: null,

		/** @property {boolean} disableOnClick - set to true to disable on click behavior (such as disabling row selection when filter is active) */
		//disableOnClick: false,

		/** @property {boolean} dynamicColumnWidths - render table without fixed width columns (only to be used in special cases) */
		//dynamicColumnWidths: false,

		/** @property {boolean} enableMassSelect - set to true to enable the checkbox, transition to multiple select mode */
		//enableMassSelect: false,

		// Number: for hierarchical tables, level to expand table on load (count starts at 1, which means don't expand past main level)
		expandLevel: 1,

		// Object {string: boolean}: map of ids to boolean indicating if id should be expanded or not when view is loaded
		//expandState: null,

		// String: controls position of filter controls
		filterPosition: constants.FILTER_POSITION_COLUMN,

		/** @property {boolean} disableAdHoc - disable and hide ad hoc and batch filters */
		//disableAdHoc: false,

		/** @property {Object<C8AttributeKey,number>} fixedColumnWidths - fixed column widths keyed by attribute key */
		fixedColumnWidths: null,

		// Boolean: indicates if view should be forced to full height rather than use given tableHeight; this is true when in composite custom (print) views, or while printing
		//forceFullHeight: false,

		/** @property {string} groupRendererClass - custom renderer class for rendering group names (currently only implemented for table views) */
		//groupRendererClass: null,

		/** @property {boolean} hasSubheaderRow - set to true if second row is rendered for matrix group subheaders */
		//hasSubheaderRow: false,

		/** @property {DOMNode} headTableNode - table element containing table header (same as bodyTableNode when using dynamic column widths) */
		headTableNode: null,

		/** @property {boolean} hideExtraColumn - set to false if view should not use an extra column to oocupy remaining horizontal space in widget container */
		//hideExtraColumn: false,

		/** @property {boolean} hideHeader - set to true to hide table header */
		//hideHeader: false,

		/** @property {boolean} hideFooter - set to true to hide footer; also disables horizontal scrolling */
		//hideFooter: false,

		/** @property {boolean} hideNestedReports - set to true to hide nested reports */
		//hideNestedReports: false,

		/** @property {boolean} hideScrollbar - set to true if view should not display a scrollbar to allow scrolling */
		//hideScrollbar: false,

		/** @property {boolean} inReactDialog - indicates if view is within a React dialog */
		//inReactDialog: false,

		/** @property {boolean} isComparing - set to true if rendering view for comparison */
		//isComparing: false,

		/** @property {boolean} lastDataColumnIsGroup - indicates if last data column is a group; used to help rendering of spanner rows */
		//lastDataColumnIsGroup: false,

		/** @property {Memory} memoryTable - holds all table cells in memory to allow quick traversing even when spanned rows and columns */
		//memoryTable: null,

		/** @property {string} newActionsButtonResource - localization resource to use for New Actions menu (rather than label of first action) */
		newActionsButtonResource: "",

		/** @property {string[]} newActionsButtonGroups - New Actions create button group labels (only used if newActionsButtonResource is specified) */
		newActionsButtonGroups: null,

		/** @property {DOMNode} newlyCreatedNode - "Newly Created" group row tr node */
		//newlyCreatedNode: null,

		// Number: number of rows to render in between timeouts - defaults to 50, but set lower if rows can take long time to render
		nRowsPerTimeout: 50,

		// Number: indicates number of columns in table
		numberOfColumns: 0,

		/** @property {number} numberOfDataColumns - number of columns in table holding data */
		numberOfDataColumns: 0,

		/** @property {string[]} placeholderActions - additional actions to be shown on "No Results" placeholder */
		//placeholderActions: [],

		/** @property {Object} placeholderClass - class to use to create table placeholder */
		placeholderClass: TablePlaceholder,

		// Boolean: indicates whether the view should be automatically scaled to the print size
		printAutoScale: true,

		/** @property {boolean} printDynamicColumns - if true, then column widths are determined automatically for printing (table-layout: auto) */
		printDynamicColumns: true,

		/**@property {boolean} printFrozenColumnsForEachPage - if true, it should repeat the frozen column on every page of the print, otherwise, it should not repeat the columns on every page, it should just show on the first page. */
		printFrozenColumnsForEachPage: false,

		// Number: percent to scale all text by, between minimum font scale and 100, applied only if no auto scaling
		printFontScale: 100,

		//Boolean: true means view is dirty and needs to be refreshed
		refreshOnSuspend: false,

		/** @property {boolean} _refreshPlugins - internal flag to force the widget to refresh its toolbar after refreshing itself */
		//_refreshPlugins: false,

		/** @property {string} relativeQueryContextUrl - current url in compare data for relative query filters */
		//relativeQueryContextUrl: null,

		/** @property {boolean} renderedOnce - if true, then view has been rendered at least once */
		//renderedOnce: false,

		/** @property {number} renderedWidth - width when view last changed size */
		//renderedWidth: null,

		/** @property {boolean} restrictGroupSelection - if true, then do not allow highlighting or selection of group rows */
		//restrictGroupSelection: false,

		// ScrollDataFixed: plugin to freeze columns and scroll table
		//scrollData: null,

		// Boolean: indicates if ad-hoc sorting should add secondary sort by name if primary sort is on another attribute
		//secondarySortByName: false,

		/** @property {boolean} selectWithHierarchy - if true, then selecting a row will select the whole hierarchy */
		//selectWithHierarchy: false,

		// Boolean: should we a show a count of nodes at the bottom of table if suspended
		//showCountIfSuspended: false,

		/** @property {boolean} skipColumnMatrices - if true, then do not add display column matrices */
		//skipColumnMatrices: false,

		// Number: number of columns to freeze
		tableFreezeColumns: constants.TABLE_DEFAULT_FREEZE_COLUMNS,

		// Boolean: Indicates that "Use automatic table height in single table view" check box will be set as true.
		tableAutomaticHeight: true,

		// String: setting that controls max height of table
		tableHeight: constants.TABLE_HEIGHT_FULL,

		// Boolean: indicates if root node of table should be shown; only valid when view configured to display root
		//tableHideRoot: false,

		// Number: height to use when displaying images (overrides imageSize if non-zero)
		tableImageHeight: 0,

		/** @property {Widget} warningTooltip - tooltip for the frozen column indicator */
		warningTooltip: null,

		/** @property {VoidFunction} _debouncedRefreshReportRows - start process of calculating and rendering report rows */
		//_debouncedRefreshReportRows: null,

		/** @property {Spec} _groupLevelSpec - spec to use for rendering group names */
		//_groupLevelSpec: null,

		/** @property {[rows: HTMLElement[], rowToInsertBefore: HTMLElement][]} _insertQueue - array of pairs of rows to insert at specific location */
		//_insertQueue: null,

		// Number: column index of first data column (1 if view allows selections)
		_nFirstColumn: 0,

		/** @property {number} _pxToEmRatio - ratio of PX to EM in table view; used when printing fixed table */
		_pxToEmRatio: 0,

		/** @property {number} _scrollCounter - number of scroll events since it started within time period */
		_scrollCounter: 0,

		// Number: scroll position to apply when view is faded back in
		//_scrollPosition: null,

		// Number: pageContent scroll position to apply when view is faded back in
		//_pageContentScrollPosition: null,

		/** @property {boolean} _resizeRowsOnNextResize - indicates if a resize has been performed since the last reload */
		//_resizeRowsOnNextResize: false,

		/** @property {Date} _scrollStartTime - time at which scroll events started */
		_scrollStartTime: null,

		// Object: map of Specs by type
		//_specsByType: null,

		/**
		 * Notify listeners of scroll event, and indicate if is coming from user, or internally by code
		 */
		_onScroll: function(e) {
			// load more content if near bottom of results
			this.queryMoreToTarget();
		},

		postCreate: function() {
			// summary: allow selection if aggregate view
			this.inherited(arguments);
			this.tableScrollNode = this.headingsNode;
			this.headTableNode = this.bodyTableNode = this.headingsNode.firstChild.firstChild;

			if (this.dynamicColumnWidths || state.get("print")) {
				this.scrollData = new _ScrollDataStub();
				this.hideScrollbar = true;
				this.hideExtraColumn = true;
			} else {
				this.scrollData = new ScrollDataFixed({view: this});

				// listen for changes to queryDisplayCount to update table footer - don't need to watch queryTotalCount as it
				// should not change without a corresponding change to queryDisplayCount; however, this will not catch all cases,
				// such as when querying for more groups which does not change either
				this.watch("queryDisplayCount", _.debounce(() => this.set("message"), 50));
			}

			// watch for changes to suspended properties
			const _onSuspended = () => this.domNode.classList.toggle("csiViewSuspended", this.isSuspended());
			this.watch("suspended", _onSuspended);
			this.watch("suspendIsOption", _onSuspended);

			// if groupRendererClass changes, then reset spec as well
			this.watch("groupRendererClass", () => this._groupLevelSpec = null);

			// watch for data changes to update report rows
			this.own(registerWriterCallback(() => this.scheduleRefreshReportRows()));

			// listen for changes to view selections to invoke select action
			this.enableRefNodeSelection(this.bodyNode);

			// data stores currently do not support charting (DataStoreNodes.chartClass)
			if (this.dataStoreDriven) {
				this.plugins = _.without(this.plugins, "charts");
			}

			if (this.bPopup) {
				// if in popup view, then charts not displayed
				html.destroyElement(this.chartNode);
				this.chartNode = null;

				// remove Export plugin
				// TODO - change Export to individual plugin rather than object
				this.plugins = _.filter(this.plugins, p => {
					return p.name !== "Export";
				});
			}

			// if in popup or dashboard, add CSS for table to be full width
			if (this.bPopup || this.bDashboard || state.get("print")) {
				domClass.add(this.tableContainerNode, "csi-table-container-full");
			}
		},

		/**
		 * @override
		 * Switch custom view preferences class if slim view
		 */
		postMixInProperties: function() {
			if (this.prefClass && this.defView.isSlim(true)) {
				this.prefClass = SlimPreferences;
			}

			// determine if view can/should be scrolled vertically
			if (!this.forceFullHeight) {
				this.set("forceFullHeight", !this.defView.getPageRelationship() || state.get("print"));
			}

			if (this.allowAutomaticHeight === undefined) {
				this.set("allowAutomaticHeight", !this.forceFullHeight && this.canScrollAutomatically());
			}

			this.inherited(arguments);
		},

		startup: function() {
			// summary: connect to form data onSelect method - postCreate was too early for some tables in wizards
			var fd = this.defView.m_formData;
			if (fd && !this._started) {
				fd.addSelectListener((ctx, url, bOn, bReplaced) => this.onSelect(ctx, url, bOn, bReplaced));

				// if content is not editable, then no need to disable all actions in body content while performing other actions;
				// it will just slow the system down
				if (!fd.m_bEditable) {
					this.bDisableContentActions = false;
				}
			}

			return this.inherited(arguments);
		},

		uninitialize: function() {
			// summary: clean up circular references
			this.scrollData.uninitialize();
			this.inherited(arguments);
		},

		/**
		 * Apply ad-hoc filters for matrix spec against unfiltered list of nodes in matrix
		 * @param {QueryResult} qr
		 * @param {Spec} spec
		 * @param {C8Node[]} nodes
		 * @return {C8Node[]}
		 */
		applyMatrixAdHocFilters(qr, spec, nodes) {
			if (!nodes.length) {
				return nodes;
			}

			const queryObject = this.getQueryResultQuery(qr.parentResult);
			const q = spec.applyMatrixFilters(new Query(), queryObject);
			const qlf = this.getQueryLevelFilter(qr.level);
			qlf?.toMatrixQuery(q, spec);
			return QueryProcessor.applyQueryToNodes(q, nodes, this.cacheView);
		},

		/**
		 * Set the TableImageHeight CSS variable for the given element
		 * @param {HTMLElement} element
		 * @param {number} height
		 */
		applyTableImageHeight(element, height) {
			html.setCSSVariable(element, "TableImageHeight", html.pixelsToStyleValue(height, this._pxToEmRatio));
		},

		canEnableButton: function(/*Button*/button, /*String*/idAction) {
			// summary: disable Save button when no selections have been made
			var fd = this.defView.m_formData;
			if (fd && domClass.contains(button.domNode, html.CSS_ENABLE_ON_CHANGE)) {
				return !fd.m_bAllowSelections || fd.hasSelections();
			}

			return true;
		},

		canGroup: function() {
			// summary: return true if view is able to group results
			// FIX: for now disable grouping if filtering is also disabled or if switchboard selection view;
			// eventually grouping could have own toolbar plugin and would be hidden based on the plugin being there
			return this.hasPlugin("tableExpand") && this.inherited(arguments);
		},

		canSort: function() {
			// summary: return true if view is able to sort results
			return this.defView.m_bSort && this.inherited(arguments);
		},

		captureHierarchyState: function() {
			// summary: store ids for rows that are currently expanded
			this.set("expandState", this._getExpandState());
		},

		/**
		 * Return true if cell spans multiple columns, even when table is not frozen
		 * @param {DOMNode} cell
		 * @return {boolean}
		 */
		cellSpansMultipleColumns: function(cell) {
			return this.memoryTable.cellSpansMultipleColumns(cell);
		},

		/**
		 * Return query level for matrix to support filtering
		 * @param {string} key
		 * @return {QueryLevel}
		 */
		createMatrixQueryLevel(key) {
			const params = this.specBuilder?.configuredMatrixParamsMap[key];
			if (params) {
				const specBuilder = new SpecBuilder(params.type, this.getContext());

				return this._createQueryLevel({
					bConfigurable: true,
					bForMatrix: true,
					qm: this,
					specBuilder,
					...params,
				});
			}

			return null;
		},

		_getEffectiveTableHeight: function() {
			// summary: return height of table based on all settings
			const {
				allowAutomaticHeight,
				bInSwitchboard,
				bPopup,
				bSwitchboard,
				forceFullHeight,
				tableAutomaticHeight,
				tableHeight,
			} = this;

			const {
				TABLE_HEIGHT_AUTO,
				TABLE_HEIGHT_FULL,
				TABLE_HEIGHT_SMALL,
			} = constants;

			// if in popup with switchboard table, or content view of switchboard, then fix to small
			if (bPopup && (bSwitchboard || bInSwitchboard)) {
				return TABLE_HEIGHT_SMALL;
			}

			// if forcing table to full height (no scrolling), or configured as full height, then use it
			if (forceFullHeight) {
				return TABLE_HEIGHT_FULL;
			}

			// force table to automatic in fullscreen mode if not a switchboard
			if (!bSwitchboard && browserStore.fullScreen) {
				return TABLE_HEIGHT_AUTO;
			}

			// ignore table height settings for single table popups, allowAutomaticHeight and tableAutomaticHeight cases
			return (allowAutomaticHeight && tableAutomaticHeight) ? TABLE_HEIGHT_AUTO : tableHeight;
		},

		getAction: function(/*String*/idAction, /*DOMNode*/element, /*Object*/params) {
			// summary: look for action on appropriate QueryLevel
			var qr = this.getQueryResultByDomNode(element);
			var a = qr && qr.level.actions && qr.level.actions.getAction(idAction);
			return a || this.inherited(arguments);
		},

		getCell: function(/*DOMNode*/dom, /*Boolean*/bHeader) {
			// summary: return td/th element that parents dom
			return html.getParentByTagName(dom, bHeader ? "th" : "td");
		},

		getColumnSpec: function(/*DOMNode*/th) {
			// summary: return spec for given column
			return this.columnData?.get(html.getDataAttribute(th, html.DATA_TABLE_SPEC_ID));
		},

		/**
		 * Return value used to match query result with that of the other view
		 * @param {Result} qr
		 * @param {boolean} isSameMaster - true when comparing revisions under the same master node
		 * @return {string}
		 */
		getCompareKeyForResult(qr, isSameMaster) {
			return "";
		},

		/**
		 * @override
		 * Return attribute level parameters for custom view, if any
		 */
		getCustomViewAttributeLevels() {
			const rels = this.defView.getRelationships(true);
			const names = this.getRelationshipNames(rels);

			return rels.map((rel, i) => ({
				name: names[i],
				specBuilder: this.createSpecBuilder(rel),
			}));
		},

		getHeadingCount: function() {
			// summary: return number of heading columns in view
			return this.numberOfDataColumns || this.inherited(arguments);
		},

		/**
		 * @override
		 * Return Heading from appropriate level
		 */
		getHeading(key, domNode) {
			if (domNode) {
				const qr = this.getQueryResultByDomNode(domNode);
				if (qr) {
					const node = qr.getNode();
					const specs = this.getQueryLevelSpecs(qr.level, node.$Type);
					const spec = specs.getByKey(key, false, false, true);
					if (spec) {
						return spec.heading;
					}
				}
			}

			return this.inherited(arguments);
		},

		/**
		 * @override
		 * Look for spec in all query levels
		 */
		getHeadingSpec(key, ignoreMatrixGroup) {
			const {columnData, columnNodeSpec, specBuilder, _queryLevels} = this;
			let spec = columnData?.getByKey(key, false, false, ignoreMatrixGroup) || specBuilder?.getCachedSpec(key, true, ignoreMatrixGroup);

			// check column node spec
			if (!spec && columnNodeSpec?.matchesKey(key)) {
				spec = columnNodeSpec;
			}

			// check other query levels
			if (!spec) {
				_queryLevels?.some((ql) => {
					if (!ql.bMain) {
						spec = ql.specBuilder?.getCachedSpec(key);
					}
					return !!spec;
				});
			}

			if (!spec) {
				// if not found anywhere, then create it
				spec = specBuilder.getCachedSpec(key, true);

				if (!spec) {
					console.warn(`Spec not found for key ${key}`);
				}
			}

			return spec;
		},

		getHeadRow: function() {
			// summary: return first row of header
			return this.headNode.firstChild;
		},

		/**
		 * Return subheader row if there is one
		 * @return {DOMNode}
		 */
		getSubheaderRow: function() {
			return this.hasSubheaderRow ? this.getHeadRow().nextSibling : null;
		},

		getLevelToCollapse: function() {
			// summary: find maximum level that has row that needs to be collapsed
			var hl = -1;

			this.getRefNodesQuery(null, csiTable.HS_MINUS, true).forEach(function(tr) {
				hl = Math.max(this.getHierarchyLevel(tr), hl);
			}, this);

			return (hl >= this.getPageLevel()) ? hl : null;
		},

		getLevelToExpand: function(/*Boolean?*/bCheckQueryLimit) {
			// summary: return minimum level that has row that can be expanded
			var hl = 100;

			this.getRefNodesQuery(null, csiTable.HS_PLUS, true).forEach(function(tr) {
				hl = Math.min(this.getHierarchyLevel(tr), hl);
			}, this);

			if (hl == 100) {
				return null;
			}

			if (bCheckQueryLimit) {
				// if querying for all (querySize == 0), then assume worst case if size not known
				const querySize = this.get("querySize") || 100000;
				let total = 0;
				let limit;

				// determine how many results need to be queried when expanding this level
				const refNodes = Array.from(this.getRefNodesQuery(hl, csiTable.HS_PLUS, true));

				const missingGroupUrls = refNodes.some(tr => {
					var qr = this.getQueryResultByDomNode(tr);
					if (qr) {
						// it is also not possible to expand the level if it contains groups and the urls per group are not known, which
						// can happen when the total number of results in the view exceeds the system query limit (i.e. 10000)
						if (qr.isGroup() && !qr.getGroupUrls()) {
							return true;
						}

						if (limit = limit || qr.level.childLevel.getResultLimit()) {
							// if store exists, then assume results already queried (may not be all results, but at least first page needed for expand);
							// if not, then add group count if group; otherwise add querySize
							if (!qr.store) {
								total += qr.predictChildCount(querySize);
							}
						}
					}

					return false;
				});

				// if there is query limit, and total exceeds limit, then return null indicating not possible to expand level
				if (missingGroupUrls || (limit && (total > limit))) {
					return null;
				}
			}

			return hl;
		},

		/**
		 * Return hierarchy level where main results exist
		 * @return {number}
		 */
		getMainLevel() {
			const count = getNumGroupingLevels(this.groupSettings);
			return this.getPageLevel() + count;
		},

		/**
		 * Return number of query levels that show table results
		 * @param {QueryLevel[]} [queryLevels]
		 * @return {number}
		 */
		getNumberOfResultLevels: function(queryLevels) {
			return _.reduce(queryLevels || this._queryLevels, (n, ql) => {
				// only count the root level if it is displayed
				if (ql.bResults && (!ql.isRootLevel() || this.isShowingRootNode())) {
					++n;

					// if level repeats, then add another so hierarchy test below will pass
					if (ql == ql.childLevel) {
						++n;
					}
				}

				return n;
			}, 0);
		},

		getPageLevel: function() {
			// summary: return level for rows generated by page relationship - ignores grouping for now
			return this.isShowingRootNode() ? 1 : 0;
		},

		getPrintProperties: function() {
			// summary: return object containing properties from view necessary to recreate it for printing that are not custom view options;
			// must copy group prefix so that groups have consistent ids and can be expanded consistent with this view
			const {groupUniqueId, query, store} = this;

			return {
				allowComparison: false,
				expandState: this._getExpandState(),
				groupUniqueId,
				query,
				store
			};
		},

		getQueryLevelSpecs: function(/*QueryLevel*/ql, /*String?*/type) {
			// summary: return Specs given source node type
			const {columnData, lastDataColumnIsGroup} = this;

			ql ||= this.getMainQueryLevel();
			type ||= ql.type;

			if (ql.specs && (type == ql.type)) {
				return ql.specs;
			}

			// separate object holds specs for node types that don't match the level type, such as for dividers and derived classes
			const obj = this._specsByType = this._specsByType || {};
			let specs = obj[type];
			if (specs) {
				return specs;
			}

			// if grouping without reporting, then render single cell to span width of table
			const {groupSpec, report, specBuilder} = ql;
			const headingCount = this.getHeadingCount() - (this.allowsSelections() ? 1 : 0);
			const spannerParams = {
				css: lastDataColumnIsGroup ? CSS_MATRIX_COLUMN_LAST : null,
				valueParams: {
					colSpan: headingCount
				},
			};
			const spannerKey = new Key({
				extendedPath: ExtendedPath.create(type),
				id: csiTable.SPANNER_TEXT,
			});
			let spannerSpec;

			if (spannerKey.reflectionAttribute) {
				spannerSpec = specBuilder.getCachedSpec(spannerKey, true, false, spannerParams);
			} else if (groupSpec && !report) {
				spannerSpec = this._getGroupLevelSpec(spannerParams);
			}

			if (spannerSpec) {
				// configure specs for nodes that have single cell to span width of table, unless frozen, in which case two cells are rendered so that frozen border exists
				specs = new Specs();

				if (this.allowsSelections()) {
					specs.add(this.getSelectionSpec());
				}

				specs.add(spannerSpec);

				if (this.get("hasQueryResultActions")) {
					specs.add(_getActionSpec());
				}

				// add final column to occupy any horizontal space remaining in the table container
				if (!this.hideExtraColumn) {
					specs.add(_getExtraSpec());
				}
			} else if (this.onlyHasOneLevel()) {
				specs = columnData;
			} else {
				specs = new Specs();
				generateSpecsForLevel(columnData, specBuilder, specs, ql.isRootLevel(), _addTableCssForGroupSpecs);
			}

			// store query level specs if same type, otherwise store in separate object
			if (type == ql.type) {
				ql.specs = specs;
			} else {
				obj[type] = specs;
			}

			return specs;
		},

		/**
		 * @override
		 * Return list of spec builders for all relationships in view, and root node if being shown in the view
		 */
		getSpecBuilders() {
			const {defView, specBuilder} = this;
			const rels = defView.getRelationships(true) || [];
			const specBuilders = rels.map((rel, i) => i ? this.createSpecBuilder(rel) : specBuilder);

			if (defView.m_root) {
				const type = this.getViewNodeType();
				const rootBuilder = new SpecBuilder(type, this.getContext(), defView.pathHintsContext, null, null, defView.m_headings);
				specBuilders.push(rootBuilder);
			}

			if (!specBuilders.length) {
				specBuilders.push(specBuilder);
			}

			return specBuilders;
		},

		getSpecValues: function(/*String*/id, /*Object*/storeOptions) {
			// summary: add appropriate context
			storeOptions.ctx = this.getContext(csi.EMPTY_URL);
			return this.inherited(arguments);
		},

		/**
		 * Return table containing head node; in fixed table case, the head is in a separate table from the body and foot
		 * @return {DOMNode}
		 */
		getHeadTableNode: function() {
			return this.headTableNode;
		},

		/**
		 * Return table containing body node; in fixed table case, the head is in a separate table from the body and foot
		 * @return {DOMNode}
		 */
		getBodyTableNode: function() {
			return this.bodyTableNode;
		},

		isRefNode: function(/*DOMNode*/e, /*Boolean*/bSelection) {
			// summary: determine if element supporting selecting
			var qr = this.getQueryResultByDomNode(e);
			return qr && (!bSelection || qr.bResultNode);
		},

		getRefNodeById: function(/*String*/id) {
			// summary: return DOMNode for given QueryResult id
			return (this.bodyNode && id) ? html.queryDom(["tr", html.getDataAttributeSelector(html.DATA_RESULT, id)], this.bodyNode, true) : null;
		},

		getRefNodeByUrl: function(/*String*/url) {
			// summary: return DOMNode associated with url - make sure to get first so that a merged matrix row is not returned
			// NOTE: be aware that the URL may not be unique in hierarchical tables, so this function should only be used where that is not the case
			return (this.bodyNode && url) ? html.queryDom(["tr", html.getDataAttributeSelector(html.DATA_URL, url)], this.bodyNode, true) : null;
		},

		getRefNodeNext: function(/*DOMNode*/refNode, /*Boolean*/bPrevious, /*Boolean*/bTestRefNode, /*Function?*/fTest) {
			// summary: find next refNode in view that matches given tests
			var level = null;

			if (refNode && !fTest) {
				// if no test given, then assume test is for row at same level or above as given refNode
				level = this.getHierarchyLevel(refNode);
				fTest = lang.hitch(this, function(r) {
					return !this.isMergedRow(r) && (this.getHierarchyLevel(r) <= level);
				});
			}

			var row = this.inherited(arguments, [refNode, bPrevious, bTestRefNode, fTest]);

			// make sure row on same level if looking for refNode using default test
			if (row && (level != null) && bTestRefNode && (this.getHierarchyLevel(row) < level)) {
				return null;
			}

			return row;
		},

		getRefNodes: function() {
			// summary: table body is only container of refNodes
			return csiTable.getResultRows(this.bodyNode);
		},

		getRefNodesQuery: function(/*Number?*/hl, /*String?*/hs, /*Boolean?*/bVisible) {
			// summary: return NodeList of rows matching given hierarchy level and/or state
			var qs = ["tr"];

			if (hl != null) {
				qs.push(html.getDataAttributeSelector(html.DATA_TABLE_LEVEL, hl));
			}

			if (hs != null) {
				qs.push(html.getDataAttributeSelector(html.DATA_TABLE_STATE, hs));
			}

			var nl = html.queryDom(qs, this.bodyNode);

			if (bVisible) {
				nl = nl.filter(html.isVisible, html);
			}

			return nl;
		},

		getRootRow: function() {
			// summary: return root row if one
			if (this.isShowingRootNode()) {
				return this.bodyNode.firstChild;
			}

			return null;
		},

		getRowNode: function(/*DOMNode*/domNode) {
			// summary: get node for row
			return this.getQueryResultByDomNode(domNode).getNode();
		},

		getRowParent: function(/*DOMNode*/row) {
			// summary: get parent of given row based on hierarchy level - make sure to skip merged rows
			if (!row) {
				return null;
			}

			var level = this.getHierarchyLevel(row);
			if (level == 0) {
				return null;
			}

			var r = row.previousSibling;

			while (this.getHierarchyLevel(r) >= level) {
				r = r.previousSibling;
			}

			return this.getRowMergeBase(r) || r;
		},

		getRowSibling: function(/*DOMNode*/row, /*Boolean?*/bSkipHierarchy) {
			// summary: get next sibling row - skipping over children and merged rows
			var l1 = this.getHierarchyLevel(row);
			var l2;

			while (row = row.nextSibling) {
				if (!this.isMergedRow(row)) {
					if (bSkipHierarchy) {
						l2 = this.getHierarchyLevel(row);

						if (l2 < l1) {
							// next row is at higher level in tree, so no more siblings
							break;
						} else if (l2 > l1) {
							// next row is at lower level in tree, so keep going
							continue;
						}
					}

					return row;
				}
			}

			return null;
		},

		getRowHasChildren: function(/*DOMNode?*/row, /*Boolean?*/bRefNode, /*Function?*/filter) {
			// summary: return true if row has child row
			return this.getRowChildren(row, bRefNode, false, true, filter).length;
		},

		getRowChildren: function(/*DOMNode?*/row, /*Boolean?*/bRefNode, /*Boolean?*/bRecursive, /*Boolean?*/bOne, /*Function?*/filter) {
			// summary: get child rows of given row
			//	if bRecursive, then collect all rows at any level under given one
			//	if bOne, then return after finding only one
			var l1 = this.getHierarchyLevel(row) + 1;
			var r = row ? row.nextSibling : this.bodyNode.firstChild;
			var rows = [];
			var l2;

			while (r) {
				if (!this.isMergedRow(r)) {
					l2 = this.getHierarchyLevel(r);
					if (l2 < l1) {
						break;
					} else if (bRecursive || (l2 == l1)) {
						// apply filter to row if one
						if ((!bRefNode || this.isRefNode(r, true)) && (!filter || filter(r))) {
							rows.push(r);
							if (bOne) break;
						}
					}
				}

				r = r.nextSibling;
			}

			return rows;
		},

		getRowMergeBase: function(/*DOMNode*/tr) {
			// summary: return row preceding merged rows and null if not a merged row
			return csiTable.getBaseMergedRow(tr);
		},

		getRowMerges: function(/*DOMNode*/row, /*Boolean*/bOmitRow) {
			return csiTable.getMergedRows(row, bOmitRow);
		},

		/**
		 * Return rows to select when given row is selected/highlighted
		 * @param {DOMNode} row
		 * @return {DOMNode[]}
		 */
		getExtraRowsToSelect: function(row) {
			row = csiTable.getBaseMergedRow(row) || row;
			let rows = [row];

			// if selecting with hierarchy, then make sure to go to root of branch and then select all children
			if (this.selectWithHierarchy) {
				const rootRow = this.getRootRow();
				let parentRow = this.getRowParent(row);

				while (parentRow != rootRow) {
					row = parentRow;
					parentRow = this.getRowParent(row);
				}

				rows = [row, ...this.getRowChildren(row, false, true)];
			} else {
				rows = [row];
			}

			// for each row, include merged (matrix) rows
			return rows.reduce((memo, r) => {
				return [...memo, ...this.getRowMerges(r)];
			}, []);
		},

		collapse: function(/*Number*/hl) {
			// summary: collapse lowest level results of table that are expanded
			this.getRefNodesQuery(hl, csiTable.HS_MINUS, true).forEach(this.collapseRow, this);
		},

		/**
		 * Collapse given row
		 * @param {DOMNode} row
		 */
		collapseRow: function(row) {
			this._setHierarchyVisibility(row, false);
		},

		/**
		 * copy group and all children from comparison view to current view; the nodes from the comparison view
		 * are recreated so that they do not render any content when first generated - the data will be added
		 * in second pass as with existing rows
		 * @param {QueryResult} comparisonResult
		 * @param {HTMLElement} rowParent
		 * @param {HTMLElement} rowNext
		 */
		copyComparisonResult(comparisonResult, rowParent, rowNext) {
			const {compareData} = this;
			const parentResult = this.getQueryResultByDomNode(rowParent);
			const {bReport, bResultNode, childResults, hl, nChildren} = comparisonResult;
			const node = compareData.copyComparisonResultNode(comparisonResult);
			const rows = [];

			const qr = this._createQueryResult({
				bCompareNode: true,
				bReport,
				bResultNode,
				hl,
				level: parentResult.level.childLevel,
				parentResult,
				parentUrl: parentResult.url,
				url: node.$URL,
			});

			const ctx = parentResult.getChildContext();
			ctx.setNodeURL(qr.url);
			ctx.setReadOnly(true);

			// generate row
			this._generateResultRow(qr, rows, ctx);

			// insert newly created row to the table
			this.insertRows(rows, null, rowNext, true);

			// recursively add children if any
			const firstRow = rows[0];

			if (nChildren) {
				// construct store as it will be populated
				qr.getStore();

				for (const child of childResults) {
					this.copyComparisonResult(child, firstRow, rowNext);
				}

				this.setHierarchyState(firstRow, csiTable.HS_MINUS);
				this.collapseRow(firstRow);
			} else {
				this.setHierarchyState(firstRow, csiTable.HS_NONE);
			}
		},

		deleteRow: function(/*DOMNode*/row, /*DOMNode?*/rowParent, /*Boolean*/bReparenting) {
			// summary: delete row from view
			var qr = this.getQueryResultByDomNode(row);
			if (!qr) {
				// if view already refreshed, then query result may be missing
				return;
			}

			const {parentResult} = qr;
			const bGroup = qr.isGroup();

			rowParent = rowParent || this.getRowParent(row);

			const _deleteResult = (rowToDelete) => {
				// remove result from store and view
				var qr = this.getQueryResultByDomNode(rowToDelete);
				if (qr) {
					this._removeQueryResult(qr, true, bReparenting);
				}

				if (qr && qr.isGroup()) {
					// remove child results from store and view
					_.each(this.getRowChildren(rowToDelete), _deleteResult);
				} else {
					// remove child DOM nodes
					this._deleteChildren(rowToDelete);
				}

				// remove row DOM nodes
				_.each(this.getRowMerges(rowToDelete), html.destroyElement);
			}

			_deleteResult(row);

			if (!this.getRowHasChildren(rowParent)) {
				if (parentResult && parentResult.isGroup() && !parentResult.isDefaultGroup()) {
					// delete parent group is no more children
					this.deleteRow(rowParent);
					// reset to null when New Created group is deleted
					if (parentResult.bNewlyCreated) {
						this.newlyCreatedNode = null;
					}
					return;
				} else {
					// remove plus/minus sign from parent if we are removing its only child
					this.setHierarchyState(rowParent);
				}
			}

			// deleting action row should not affect parent reports or groups
			if (!qr.bAction) {
				this._onChangeRowChildren(rowParent);
			}

			if (bGroup) {
				// if the table is now empty, then generate action row same as when table is loaded from scratch with no groups
				var qrRoot = this.getQueryResult();
				if ((qrRoot.getChildDisplayCount() === 0) && this.hasEmptyQuery()) {
					this._removeGroupLevels();

					var actionRow = this.generateActionRow(qrRoot);
					if (actionRow) {
						this.insertRows([actionRow]);
					}
				}

				// make sure footer message is updated when a group
				this.set("message");
			}

			// check if we need to load more results
			this.queryMoreIfNeeded();

			// hide body if no more rows
			this.updatePlaceholder();
		},

		editHeading: function(/*DOMNode*/rootNode, /*Boolean*/bTimeout, /*Object?*/params) {
			// summary: if node is for actions, then present drop-down of possible actions
			const row = this.getRefNode(rootNode);
			const cell = (rootNode === row) ? this.getTableCellAtIndex(row, 0) : rootNode;

			if (this.isActionRow(row) && cell) {
				this.scrollIntoView(cell);

				// enable the widget to put focus on action row
				this.set("disabled", false);
				var btn = registry.byNode(cell.lastChild);
				btn.focus("start");
				return false;
			} else {
				// this needs to be done here, otherwise the action controller will not be invoked on a hidden element
				this.scrollData.scrollHorizontalToCell(cell);
				return this.inherited(arguments);
			}
		},

		/**
		 * Expand each item at given level in table that passes filter function; if level is null, then expand for all levels
		 * @param {number} [hl]
		 * @param {(tr: HTMLElement) => boolean} [filterFunc]
		 * @return {Deferred}
		 */
		expand(hl, filterFunc) {
			return this._load(() => this._expand(hl, filterFunc).then(() => this._endView(), (err) => this.onViewError(err)));
		},

		/**
		 * Expand given row
		 * @param {DOMNode} row
		 * @return {Promise|null}
		 */
		expandRow: function(row) {
			const promise = this._setHierarchyVisibility(row, true);
			this.when(promise, () => this.scheduleRefreshReportRows());
			return promise;
		},

		_endView: function() {
			/**
			 * C8-20402 Fix Horizontal Scrolling on Safari (also C8-20843, C8-21481)
			 * This strange code is supposed to fix horizontal table scrolling issue on Safari browser
			 * This fix triggers "display: flex" attribute off, then force browser to recalculate dom and then trigger it back on
			 */
			if (has("safari") && !this._bSafariFixApplied) {
				this._bSafariFixApplied = true;

				// "this.domNode" represents the table container, would cover both React and Dojo case
				const jDomNode = $(this.bPopup ? this.domNode : ".csi-split-pane");
				jDomNode.addClass("csi-fix-display-safari");

				// Second argument used to force browser to recalculate dom. Use it as argument to prevent optimization
				jDomNode.removeClass("csi-fix-display-safari", jDomNode.width());
			}

			// check for outdated report rows - processing does not have to be done before loading is finished;
			// when printing, this step is done as part of _postViewDeferred, as it must finish before view is completely loaded
			if (!state.get("print")) {
				this.scheduleRefreshReportRows();
			}
			this.tableShowWait(false);

			this.inherited(arguments);

			// check if we need to load more results
			this.queryMoreIfNeeded();
		},

		/**
		 * Expand table to given hierarchy level Internal processing for expand function
		 * @param {number} [hl] - hierarchy level to expand to; if not provided, then table expands to view main level
		 * @param {(tr: HTMLElement) => boolean} [filterFunc] - function determining which rows to expand
		 * @return {Deferred}
		 */
		_expand(hl, filterFunc) {
			var dfd = new Deferred();
			var nTotal = 0;
			var nComplete = 0;
			var bAll;

			if (hl == null) {
				bAll = true;
				hl = this.getPageLevel();
			}

			const _reject = (err) => {
				this._flushInsertQueue();

				if (err instanceof QueryLimitError) {
					// if query limit exceeded, then do not expand by default
					dfd.resolve();
				} else {
					dfd.reject(err);
				}
			};

			const _processLevel = () => {
				var bRecurse;
				var dfdExpand;
				var i = 0;

				// find all visible rows on this level
				var nl = this.getRefNodesQuery(hl, null, true);

				const _processNextRow = () => {
					// expand rows one by one, freeing the browser each time so that it does not lock up when expanding many rows
					if (this.isValidView()) {
						var tr = nl[i++];
						if (tr) {
							when(this._setHierarchyVisibility(tr, true),
								() => {
									topic.publish("/csi/table/expand", ++nComplete / nTotal);
									_.defer(_processNextRow);
								},
								_reject
							);

							return;
						}

						this._flushInsertQueue();

						if (bRecurse) {
							++hl;
							_.defer(_processLevel);
							return;
						}
					}

					dfd.resolve();
				}

				// narrow the rows to consider to those that match the given filter function
				if (filterFunc) {
					nl = nl.filter(filterFunc);
				}

				// it is possible there were no rows to expand on this level, but are rows to expand on deeper levels
				bRecurse = bAll && !!nl.length;

				// find rows to expand
				nl = nl.filter((tr) => this.getHierarchyState(tr) == csiTable.HS_PLUS);

				// expand rows on this level that are not yet expanded
				if (nl.length) {
					nTotal += nl.length;

					// if at group level and group does not know child urls, then need to first query for them
					dfdExpand = this._onExpandLevelIfGroup(nl) || new Deferred().resolve();
					dfdExpand = dfdExpand.then(() => this._onExpandLevel(nl));
				}

				// improve performance by waiting to insert rows into the DOM until all branches on a given level are expanded
				this._insertQueue = [];

				when(dfdExpand, _processNextRow, _reject);
			}

			_processLevel();

			return dfd;
		},

		/**
		 * Return true if view is configured with only one level - this disregards any grouping levels
		 * @return {boolean}
		 */
		onlyHasOneLevel() {
			const {defView} = this;
			const relationships = defView.getRelationships(true);
			const n = relationships?.length || 0;
			return defView.m_root ? !n : (n === 1);
		},

		/**
		 * Scroll back to previous position, if any
		 * @return {Promise<void>}
		 */
		async restoreScrollPosition() {
			const {_queryDisplayCountTargetSaved, queryDisplayCount} = this;
			const target = _queryDisplayCountTargetSaved;
			this._queryDisplayCountTargetSaved = 0;

			// restore display count target - avoid re-doing "more" after refreshDataRows()
			if (target > queryDisplayCount) {
				this._set("queryDisplayCountTarget", target);

				// if table is still loading then wait until it finish loading and only then query to target
				await this.getDeferred();
				await this.queryMoreToTarget(true);
				this.restoreScrollPosition();
			} else {
				// restore table scroll
				if (this._scrollPosition != null) {
					this.tableScrollNode.scrollTop = this._scrollPosition;
					this._scrollPosition = null;
				}

				// restore page content scroll
				if (this._pageContentScrollPosition != null) {
					html.pageContentScroll().scrollTop = this._pageContentScrollPosition;
					this._pageContentScrollPosition = null;
				}
			}
		},

		/**
		 * Save scroll position of table so that it can be restored later
		 */
		saveScrollPosition: function() {
			// if table is empty, then no need to save scroll position
			if (!this.bodyNode.firstChild) {
				return;
			}

			// save table scroll
			if (this._scrollPosition == null) {
				this._scrollPosition = this.tableScrollNode.scrollTop;
			}

			// save page content scroll
			if (this._pageContentScrollPosition == null) {
				this._pageContentScrollPosition = html.pageContentScroll().scrollTop;
			}

			// preserve number of results already loaded - avoid re-doing "more" after refreshDataRows()
			if (this.bPreserveQueryDisplayCount) {
				this.bPreserveQueryDisplayCount = false;
				this._queryDisplayCountTargetSaved = this.queryDisplayCountTarget;
			}

		},

		findNextHeadingNode: function(/*DOMNode*/node, /*String*/key, /*Boolean*/bShift) {
			// summary: find next cell to edit and make sure it is visible in scrolled view
			var self = this;

			function _canEdit(row, cell) {
				// test for hidden row because it could be filtered out or collapsed
				if (self.isRowVisible(row)) {
					// action row is always editable
					if (self.isActionRow(row) && (cell == row.firstChild) && registry.byNode(cell.lastChild)) {
						return true;
					}

					// otherwise, return true if cell holds heading with inline action
					if (csiHeading.isHeadingNodeTabStop(cell)) {
						return true;
					}
				}

				return false;
			}

			return this.getNextCell(node, _canEdit, (key == keys.ENTER), bShift, true);
		},

		/**
		 * @override
		 * Table body needs to be redrawn when forced to be read-only
		 */
		forceReadOnly() {
			this.inherited(arguments);
			this.redrawData();
		},

		/**
		 * Freeze table with given header as frozen column
		 * @param {number} frozenColumn
		 */
		freezeTable: function(frozenColumn) {
			const {
				scrollData,
				warningTooltip
			} = this;

			if (scrollData.isFrozen() && (scrollData.nFrozen != frozenColumn)) {
				// the number of columns is one greater than the index
				this.set("tableFreezeColumns", frozenColumn + 1);

				// recalculate the actual frozen column based on the new value
				const newActualFrozenColumn = this.scrollData._calculateFreezeColumn();

				if (frozenColumn > newActualFrozenColumn) {
					const cell = this.getTableCellAtIndex(this.getHeadTableNode().rows[0], frozenColumn);

					if (!warningTooltip) {
						this.warningTooltip = new Tooltip({
							connectId: [cell],
							position: ["above-alt"],
							label: locale.resource("ViewFrozenWarningMessage")
						});
					}

					this.warningTooltip.open(cell);
				}

				this._scroll();
			}
		},

		/**
		 * Generate table body cell
		 * @param {Spec} spec 
		 * @param {HTMLTableRowElement} [tr] 
		 * @return {HTMLTableDataCellElement}
		 */
		generateCell(spec, tr, applyCss) {
			return html.create("td", spec?.valueParams, tr);
		},

		/**
		 * Generate TH node for table head
		 * @param {Context} ctx
		 * @param {Spec} spec
		 * @param {Spec} specSub
		 * @param {DOMNode} tr - parent node for header
		 * @param {SpecHeaderParams} [params] - if specified, then use parameters to render content as well
		 * @return {DOMNode}
		 */
		generateHeaderCell: function(ctx, spec, specSub, tr, params) {
			const cache = this.cacheView;
			const columnNode = cache.get(spec.columnUrl);
			const lowestSpec = specSub || spec;
			const bStatic = ctx.bPrint || ctx.bExcel || this.dynamicColumnWidths;
			const cellParams = bStatic ? (params || {}) : lowestSpec.getHeaderCellParams(params);

			// FIX: this is messy - need to work on better way to add up all css for a header and sub-header;
			// why doesn't all of this get processed into headerParams?
			const classes = [spec.css];
			const h = spec.heading;
			if (h) {
				if (columnNode) {
					classes.push(def.getCSS(h, null, columnNode, cache, ctx));
				} else {
					classes.push(h.m_class);
				}
			}

			if (lowestSpec.isCenterAligned()) {
				classes.push(csiTable.CSS_HEADER_CENTER);
			} else if (lowestSpec.isRightAligned()) {
				classes.push(csiTable.CSS_HEADER_RIGHT);
			}

			if (specSub) {
				classes.push(specSub.css);

				if (specSub.heading) {
					classes.push(specSub.heading.m_class);
				}
			}

			if (cellParams.className) {
				classes.push(cellParams.className);
			}

			cellParams.className = _.compact(classes).join(" ");

			// finally, create header cell
			const th = html.create("th", cellParams, tr);

			// if parameters were passed and not printing, then create structure in cell for content and sort indicator
			if (params && !bStatic) {
				const contentParams = lowestSpec.getHeaderContentParams(params);

				if (!_.isEmpty(contentParams)) {
					const div = html.create("div", {"className": csiTable.CSS_HEADER_CONTAINER}, th);
					contentParams.className = csiTable.CSS_HEADER_CONTENT;
					html.create("span", contentParams, div);

					if (spec.canSort()) {
						html.create("div", {className: CSS_HEADER_ARROW}, div);
					}
				}
			}

			return th;
		},

		generateRow: function(/*Node|Node[]*/nodes, /*QueryResult?*/qrParent, /*Context?*/ctx) {
			// summary: generate rows for given nodes
			if (!(nodes instanceof Array)) {
				nodes = [nodes];
			}

			qrParent = qrParent || this.getQueryResult();
			var ql = qrParent.level.childLevel;
			var hl = qrParent.hl + 1;
			var rows = [];
			var bReport = Boolean(ql.groupSpec && ql.childLevel.report);

			ctx = ctx || qrParent.getChildContext();
			ctx.preRollup();

			ctx.forEach(nodes, function(ctx) {
				var qr = this._createQueryResult({
					bReport,
					bResultNode: true,
					hl,
					level: ql,
					parentResult: qrParent,
					parentUrl: qrParent.url,
					url: ctx.getNodeURL()
				});

				this._generateResultRow(qr, rows, ctx);
			}, this);

			ctx.postRollup();
			return rows;
		},

		/**
		 * Return available height for table in container
		 * @return {number}
		 */
		getAvailableTableHeight: function() {
			// in popup, maxHeight on scroll node being set in csi/view/ViewDialog->resize()
			const h = this.bPopup && this.scrollNode && this.scrollNode.style.maxHeight;
			if (h) {
				return parseInt(h);
			}

			// in browser window, need to allow room for horizontal scrollbar below table
			return html.getAvailableHeightWithScrollbarAllowance(this.tableContainerNode);
		},

		/**
		 * Return available width for table in container
		 * @param {boolean} [ignorePopup] - if true and in popup view, then return view container width as-is
		 * @return {number}
		 */
		getAvailableTableWidth: function(ignorePopup) {
			const {
				bPopup,
				domNode,
				hideHeader,
				viewParent
			} = this;

			let minWidth = 0;

			if (bPopup) {
				// in popup, maxWidth on scroll node being set in csi/view/ViewDialog->resize();
				// if popup contains a table switchboard, then the content view needs to check the parent
				// view's scrollNode rather than its own (Enumerations popup from BO attribute dialog)
				const scrollNode = viewParent ? viewParent.scrollNode : this.scrollNode;
				const scrollWidth = !ignorePopup && scrollNode && scrollNode.style.maxWidth;

				if (scrollWidth) {
					return parseInt(scrollWidth);
				}

				minWidth = hideHeader ? 0 : TABLE_MIN_WIDTH;
			}

			// offsetWidth property will round the value to an integer. But we need precise value, therefore using getBoundingClientRect;
			const widthWidthFraction = domNode.getBoundingClientRect().width;
			return Math.max(widthWidthFraction, minWidth);
		},

		/**
		 * Update visibility of table placeholder
		 */
		updatePlaceholder: function() {
			this.scrollData.updatePlaceholder();
		},

		/**
		 * Return true if there are filters on columns
		 * @return {boolean}
		 */
		_hasColumnFilter: function() {
			return !_.isEmpty(this.query);
		},

		/**
		 * Return true if there are filters on the view
		 * @return {boolean}
		 */
		_hasViewFilter: function() {
			return !_.isEmpty(this.filterSettings);
		},

		/**
		 * Return no result placeholder type, return null/undefined to remove/do_not_create the placeholder
		 * @return {string}
		 */
		_getNoResultType: function() {
			return NORESULT;
		},

		/**
		 * Return no result placeholder description
		 * @param {boolean} hasColumnFilter
		 * @param {boolean} hasNewAction
		 * @return {string}
		 */
		_getNoResultDescription: function(hasColumnFilter, hasNewAction) {
			var nodeType = this.getResultNodeType();
			var nodeName = locale.resource("TYPE_DEFAULT").toLowerCase();

			if (nodeType) {
				nodeName = locale.type(nodeType).toLowerCase();
			}

			if (hasColumnFilter && !hasNewAction) {
				return locale.resource("ViewPlaceholderDescription" + NORESULT);
			} else if (hasColumnFilter) {
				return locale.resource("ViewPlaceholderDescriptionNoResultWithTwoOptions", [nodeName]);
			} else if (hasNewAction) {
				return locale.resource("ViewPlaceholderDescriptionNoResultWithParameter", [nodeName]);
			}

			return "";
		},

		/**
		* Return true if query result has group rows
		* @return {boolean}
		*/
		hasGroupRows: function() {
			return _.some(this.getQueryResults(), function(qr) {
				return qr.isGroup();
			});
		},

		/**
		* Return true if query result has result rows
		* @return {boolean}
		*/
		hasResultRows: function() {
			const results = this.getQueryResults().filter((qr) => qr.bResultNode);
			const nResults = results.length;

			// the root query result will always exist, but will not be rendered unless there is a root node of the view;
			// in some cases the root node is null, and so nothing is displayed. Make sure to return false in such cases.
			if (this.isShowingRootNode() && !this.getViewNode() && (nResults === 1)) {
				return false;
			}

			return !!nResults;
		},

		/**
		 * Return index of header cell that is or contains the element;
		 * if a matrix header, then returns the last index of the matrix
		 * @param {DOMNode} element
		 * @return {number}
		 */
		getHeaderElementMaxIndex: function(element) {
			let th = element.closest("TH");
			if (!th) {
				return -1;
			}

			const {memoryTable} = this;
			const firstRow = this.getHeadRow();
			const tr = th.parentNode;
			let index = memoryTable.getCellMaxIndex(th);

			if (tr != firstRow) {
				th = memoryTable.getCellAtIndex(firstRow, index);
				index = memoryTable.getCellMaxIndex(th);
			}

			return index;
		},

		getHierarchyLevel: function(/*DOMNode*/row) {
			// summary: return hierarchy level for given row
			return csiTable.getRowHierarchyLevel(row);
		},

		getHierarchyState: function(/*DOMNode*/row) {
			// summary: return hierarchy state for given row
			return html.getDataAttribute(row, html.DATA_TABLE_STATE);
		},

		/**
		 * Find next cell that passes filter if provided
		 * @param {DOMNode} cell - starting cell
		 * @param {Function|null} filter - optional function taking row and cell nodes as arguments
		 * @param {boolean} bEnter - if true, then iterate vertically rather than horizontally (Tab)
		 * @param {boolean} bReverse - if true (Shift key pressed), then iterate backwards
		 * @param {boolean} bWrap - if true, then wrap to next row or column, or even back to start of table
		 */
		getNextCell: function(cell, filter, bEnter, bReverse, bWrap) {
			const memoryTable = this.memoryTable;
			const nCols = this.numberOfColumns;

			var row = cell.parentNode;
			var container = row.parentNode;
			var nextCell;
			var nCol = memoryTable.getCellIndex(cell);

			const nMinCol = bReverse ? nCols - 1 : 0;
			const nMaxCol = bReverse ? 0 : nCols - 1;
			const inc = bReverse ? -1 : 1;
			const sibling = bReverse ? "previousSibling" : "nextSibling";
			const resetRow = bReverse ? container.lastChild : container.firstChild;

			filter = filter || Boolean;

			const increment = bEnter ?
				function vertical() {
					row = row[sibling];

					if (!row && bWrap) {
						row = resetRow;

						if (nCol !== nMaxCol) {
							nCol += inc;
						} else {
							nCol = nMinCol;
						}
					}

					nextCell = row ? memoryTable.getCellAtIndex(row, nCol, nCol - 1) : null;
				} :
				function horizontal() {
					if (nCol !== nMaxCol) {
						nCol += inc;
					} else if (bWrap) {
						nCol = nMinCol;
						row = row[sibling];

						if (!row) {
							if (bWrap) {
								row = resetRow;
							} else {
								return null;
							}
						}
					} else {
						row = null;
					}

					nextCell = row ? memoryTable.getCellAtIndex(row, nCol, nCol - 1) : null;
				};

			increment();

			while (row && (nextCell != cell)) {
				if (nextCell && filter(row, nextCell)) {
					return nextCell;
				}

				increment();
			}

			return null;
		},

		/**
		 * Return index of cell taking into account colspan and rowspan
		 * @param {DOMNode} td
		 * @return {number}
		 */
		getTableCellIndex: function(td) {
			return this.memoryTable.getCellIndex(td);
		},

		/**
		 * Return cell at given index taking into account colspan and rowspan
		 * @param {DOMNode} tr
		 * @param {number} n
		 * @return {DOMNode}
		 */
		getTableCellAtIndex: function(tr, n) {
			return tr ? this.memoryTable.getCellAtIndex(tr, n) : null;
		},

		hasChart: function() {
			// summary: return true if there is a chart in the view
			return !_.isEmpty(this.chartSettings);
		},

		hasContent: function() {
			// summary: return true if view is showing content to print/export - allow if only groups are being shown (collapsed)
			var qr = this.getQueryResult(null, true);
			return qr && (qr.getChildDisplayCount() !== 0);
		},

		hasMatrix: function() {
			// summary: return true if table currently displaying a matrix
			let bMatrix;

			this.columnData?.traverse(false, (c) => {
				bMatrix ||= c.isMatrix;
			});

			return !!bMatrix;
		},

		highlight: function(/*DOMNode*/node, /*Boolean*/bOff, /*Boolean*/bSuspend, /*DOMNode[]?*/extras) {
			// summary: highlight merged rows as well as given one
			if (this.restrictGroupSelection && this.isGroupRow(node)) {
				return;
			}

			if (node) {
				extras = this.getExtraRowsToSelect(node);
			}

			this.inherited(arguments, [node, bOff, bSuspend, extras]);
		},

		/** indicate if in compare mode */
		inCompareMode() {
			const {pageStore} = rootStore;
			return pageStore && pageStore.isCompareMode;
		},

		insertRows: function(/*DOMNode[]*/rows, /*DOMNode?*/rowParent, /*DOMNode?*/rowNext, /*Boolean*/bNew) {
			// summary: add rows to table structures
			if (!rows?.length) {
				return;
			}

			const {_insertQueue} = this;
			const qrNext = bNew && rowNext && this.getQueryResultByDomNode(rowNext);

			rows.forEach((row) => {
				this._preInsertRow(row, bNew, qrNext);
			});

			if (_insertQueue) {
				_insertQueue.push([rows, rowNext]);
			} else {
				// update row for scrolling before being inserted into the DOM
				this.scrollData.fixTableRows(rows, false);
				this._insertRowsIntoDom(rows, rowNext);
			}

			// notify parent of change to children if new rows
			if (bNew) {
				rowParent = this._onChangeRowChildren(rowParent);
			}

			return rowParent;
		},

		/**
		 * Add rows in insert queue into table
		 */
		_flushInsertQueue() {
			const {_insertQueue} = this;

			if (!_insertQueue) {
				return;
			}

			// update all rows for scrolling at once
			const allRows = _insertQueue.map(([rows,]) => rows);
			this.scrollData.fixTableRows(_.flatten(allRows), false);

			_insertQueue.forEach(([rows, rowToInsertBefore]) => {
				this._insertRowsIntoDom(rows, rowToInsertBefore);
			});

			this._insertQueue = null;
		},

		/**
		 * Insert rows into DOM in front of given row (if none, then append)
		 * @param {HTMLElement[]} rows
		 * @param {HTMLElement} [rowToInsertBefore]
		 */
		_insertRowsIntoDom(rows, rowToInsertBefore) {
			const {bodyNode} = this;

			rowToInsertBefore = this._validateRowInTable(rowToInsertBefore);

			if (rowToInsertBefore) {
				// insert before given row
				rows.forEach((row) => {
					bodyNode.insertBefore(row, rowToInsertBefore);
					this.onInsertBodyRow(row);
				});
			} else {
				// insert at the end of the table
				rows.forEach((row) => {
					bodyNode.appendChild(row);
					this.onInsertBodyRow(row);
				});
			}
		},

		/**
		 * Update table internal structures before inserting rows into DOM
		 * @param {HTMLElement} row
		 * @param {boolean} [bNew] - indicates if rows is new (versus replacing an existing row)
		 * @param {QueryResult} [qrNext] - query result to insert before
		 * @param {boolean} [isReplace] - indicate if replacing rows in which case no need to update query results
		 */
		_preInsertRow(row, bNew, qrNext, isReplace) {
			const qr = this.getQueryResultByDomNode(row);
			const bAction = qr?.bAction;
			const bGroup = qr?.isGroup() && this.restrictGroupSelection;

			this._addRowToMemoryTable(row);

			// allow custom styling per row
			this.onRowStyling(row, qr);

			// enable row selection for table rows except when selecting rows - row selection will happen differently there
			if (qr && !bAction && !bGroup && !this.allowsSelections()) {
				this.enableRefNodeHighlighting(row);
			}

			// inform view of new result
			if (!isReplace && !this.isMergedRow(row)) {
				this._insertQueryResult(qr, bNew, qrNext);
			}
		},

		/**
		 * If the given row is not in the table, then find it again
		 * @param {HTMLElement} row
		 * @return {HTMLElement}
		 */
		_validateRowInTable(row) {
			return (row && !row.parentNode) ? this.getRefNodeById(this.getRefNodeId(row)) : row;
		},

		/**
		 * Return true if row represents an action row
		 * @param {DOMNode} row
		 * @return {boolean}
		 */
		isActionRow: function(row) {
			var qr = this.getQueryResultByDomNode(row);
			return qr && qr.bAction;
		},

		/**
		 * Return true if table is grouped table and it is grouped by sections (BOM/Placements)
		 * @param {boolean} [allowTwoGroups] - if true, then it is ok for 2 levels of grouping so long as section is at first level
		 * @return {boolean}
		 */
		isGroupBySection: function(allowTwoGroups) {
			return false;
		},

		/**
		 * Return true if row represents a table group
		 * @param {DOMNode} row
		 * @return {boolean}
		 */
		isGroupRow: function(row) {
			var qr = this.getQueryResultByDomNode(row);
			return qr && qr.isGroup();
		},

		/**
		 * Return true if row is part of a matrix and is part of a row span
		 * @param {DOMNode} row
		 * @return {boolean}
		 */
		isMergedRow: function(row) {
			return csiTable.isMergedRow(row);
		},

		/**
		 * Return true if row is a report row
		 * @param {DOMNode} row
		 * @return {boolean}
		 */
		isReportRow: function(row) {
			const qr = this.getQueryResultByDomNode(row);
			return qr && qr.bReport;
		},

		/**
		 * Return true if row is not collapsed
		 * @param {DOMNode} row
		 * @return {boolean}
		 */
		isRowVisible: function(row) {
			return row && (domStyle.get(row, "visibility") == "visible");
		},

		/**
		 * Return true if displaying root row
		 * @return {boolean}
		 */
		isShowingRootNode: function() {
			return this.defView.m_root && !this.tableHideRoot;
		},

		/**
		 * Extension point for inserting behavior after table settings are fully resolved and before table is rendered
		 * @param {Context} ctx
		 * @param {Deferred[]} dfds
		 */
		onBuildColumns: function(ctx, dfds) { },

		/**
		 * Extension point for inserting behavior necessary to resolve columns in table
		 * @param {Context} ctx
		 * @param {Deferred[]} dfds
		 */
		onBuildDeferreds: function(ctx, dfds) { },

		/**
		 * Extension point for modifying table header
		 * @param {Context} ctx
		 * @param {DOMNode[]} rows
		 */
		onBuildHead: function(ctx, rows) { },

		/**
		 * @override
		 * Data stores do not yet support all table features
		 */
		onCreateDefaultCustomView(cv) {
			const {bSupportsExpandLevelOption, dataStoreDriven, defView, specBuilder} = this;

			this.inherited(arguments);

			if (dataStoreDriven) {
				// remove charting and count options as not yet supported by data store
				cv.removeCustomOption("chartHeight");
				cv.removeCustomOption("chartHideTable");
				cv.removeCustomOption("chartSettings");
				cv.removeCustomOption("showCountIfSuspended");
			}

			cv.setValue("filterRemember", api.getDefaultRememberAdHocFilter());

			if (!defView.m_root) {
				cv.removeCustomOption("tableHideRoot");
			}

			if (!bSupportsExpandLevelOption) {
				cv.removeCustomOption("expandLevel");
			}

			const n = defView.getRelationships(true)?.length || 0;
			if (!this.canReport() || n < 2) {
				cv.removeCustomOption("hideNestedReports");
			}

			if (this.allowAutomaticHeight === false) {
				cv.removeCustomOption("tableAutomaticHeight");
			}

			cv.setValue("sortSettings", this.loadDefaultSortSettings());

			// provide custom view with mapping of matrix target types to key
			cv.mapMatrixTypeToKey = specBuilder.configuredMatrixTypeToKeyMap;
		},

		/**
		 * Extension point for updating context before rendering result row (pre-calculations)
		 * @param {Context} ctx
		 */
		onGenerateResultRow(ctx) { },

		/**
		 * @override
		 * Resizing of container should trigger resize of widget
		 */
		onViewEnd() {
			if (!this.renderedOnce) {
				this.renderedOnce = true;

				// this is necessary to resize the table when the table is in a composite view (not automatic height) and
				// its content grows such that it now caused overflow on the container; when a scrollbar is introduced,
				// it will require the table to be re-frozen
				const resizeObserver = new ResizeObserver((entries) => {
					const tableHeight = this._getEffectiveTableHeight();

					if (!csiTable.isAutomaticHeight(tableHeight) || this.isPreview) {
						const contentRect = entries && entries[0] && entries[0].contentRect;
						const {renderedWidth} = this;

						if (contentRect) {
							// we are only worried about width here, as non-automatic height tables already have a max height;
							// if the height of the container changes, that shouldn't affect such tables
							// NOTE: the height can change (very) slightly as the horizontal slider is used, and it looks like images are
							// to blame. So just checking width to avoid a resize while scrolling horizontally, since the resize will destroy
							// the slider and cause errors.
							const newWidth = Math.ceil(contentRect.width);

							if (renderedWidth && (newWidth === renderedWidth)) {
								return;
							}

							this.renderedWidth = newWidth;
						}

						this.resize();
					}
				});

				resizeObserver.observe(this.domNode);

				this.own({
					remove: () => {
						resizeObserver.disconnect();
					}
				});
			}

			if (this._refreshPlugins) {
				this.toolbarNode?.enablePlugins();
				this._refreshPlugins = false;
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Ensure all actions in body are handled
		 */
		onViewDisable: function(disabled) {
			// apply disabled state to inline menus
			query(".tableViewAction", this.bodyNode).forEach(function(n) {
				var menu = registry.byNode(n);
				menu.closeDropDown();
				menu.set("disabled", disabled);
			});

			// if body is currently hidden, then still need to enable actions so that when it is shown it is correct
			if (!disabled && this.bDisableContentActions && !this.bodyNode.parentNode) {
				html.queryDom([".", html.CSS_ACTION], this.bodyNode).forEach(function(dom) {
					html.setDisabled(dom, disabled);
				});
			}
		},

		/**
		 * @override
		 * Allow matrix classes in view to initialize, which could include adding toolbar plugins
		 */
		onViewPlugins() {
			this.configuredHeadings?.forEach((h) => h.getMatrixProcessor()?.initializeView(this));
		},

		/**
		 * @override
		 * Derive image size and create wrapper node for images if height and width explicitily set in custom view
		 */
		onViewPreferences: function() {
			this.inherited(arguments);

			if (this.tableImageHeight) {
				this.set("imageSize", getImageSizeBySize(this.tableImageHeight, this.tableImageHeight));
			}
		},

		/**
		 * If table view has charts, then separate out into own page block for printing
		 */
		onViewPrint: function() {
			var chartNode = this.chartNode;
			if (!chartNode || !chartNode.firstChild) {
				return;
			}

			var printView = this.getToolbarContainerView(true);
			var compositeNode;
			var chartPageNode;

			if (printView) {
				// create page block before table view
				compositeNode = chartNode.closest("." + html.CSS_PAGE);
				chartPageNode = html.create("div", {"class": html.CSS_PAGE}, compositeNode, "before");

				// copy view title
				var titleNode = this.domNode.previousSibling;
				if (titleNode) {
					chartPageNode.appendChild(titleNode.cloneNode(true));
				}
			} else {
				// create composite view to wrap charts (as gallery view) and table view
				compositeNode = html.create("div", null, this.domNode.parentNode);
				chartPageNode = html.create("div", {"class": html.CSS_PAGE}, compositeNode);

				html.setDataAttribute(compositeNode, html.PRINT_SPLITTER, "composite");
				html.create("div", {"class": html.CSS_PAGE}, compositeNode).appendChild(this.domNode);
			}

			html.setDataAttribute(chartNode, html.PRINT_SPLITTER, "gallery");
			chartPageNode.appendChild(chartNode);
			$(chartNode).addClass(html.CSS_VIEW).children().addClass(csiGallery.CSS_BLOCK);
		},

		/**
		 * Extension point called when results for table query are known
		 * @param {QueryResult} qr - query result requesting child nodes
		 * @param {Node[]} resultNodes
		 */
		onViewResults: function(qr, resultNodes) { },

		/**
		 * @override
		 * Adjust settings before starting to load view
		 */
		onViewStart: function(dfds) {
			this.tableShowWait(true);

			// reset reporting status before rendering view
			const bPrint = state.get("print");
			const printDynamicColumns = (bPrint && this.printDynamicColumns);
			this.dynamicColumnWidths = this.dynamicColumnWidths || printDynamicColumns;
			this.bReporting = false;

			// if view starts out suspended, then make sure it is refreshed when unsuspended
			this.set("refreshOnSuspend", this.isSuspended());

			// allow incremental loading with fixed column widths and no grouping when using a scrolling table
			let incrementalQuerySize = 0;

			if (!this.querySizeFixed && _.isEmpty(this.groupSettings) && (this._getEffectiveTableHeight() != constants.TABLE_HEIGHT_FULL)) {
				incrementalQuerySize = INCREMENTAL_QUERY_SIZE;
			}

			this.set("incrementalQuerySize", incrementalQuerySize);

			// set variable that will be used to cap images to configured height
			if (!printDynamicColumns || this.tableImageHeight) {
				this._pxToEmRatio = bPrint ? html.pixelTo(html.EM, this.domNode) : 0;
				this.applyTableImageHeight(this.tableContainerNode, this._getTableImageHeight());
				const imageContainerHeight = this.tableImageHeight ? html.pixelsToStyleValue(this.tableImageHeight, this._pxToEmRatio) : "unset";
				html.setCSSVariable(this.tableContainerNode, "TableImageContainerHeight", imageContainerHeight);
			}
		},

		/**
		 * Refresh all data rows passing an optional filter function
		 * @param {Function} [filterFunc] - function takes row as a parameter returning boolean
		 * @param {boolean} [bQuery] - pass true to query for any missing data on the rows
		 * @return {Deferred}
		 */
		refreshDataRows: function(filterFunc, bQuery) {
			// it's a lot quicker to rebuild the table from scratch, then replace rows one by one
			if (!filterFunc) {
				return this.redrawData();
			}

			var rows = [];

			this.traverseDataRows((row) => {
				rows.push(row);
			}, filterFunc, true);

			return this._refreshRows(rows, bQuery);
		},

		refreshHeadingCell: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*Boolean*/bPendingChange) {
			// if the table is in comparison mode, then refresh the row rather than just the cell so that comparison values are included
			if (this.isComparing) {
				const row = this.getRefNode(refNode);
				this._refreshRow(row, true);
				return;
			}

			// make sure CSS classes added by table are maintained after refresh
			const classes = _.filter(csiTable.getCellClasses(), function(css) {
				return domClass.contains(refNode, css);
			});

			// add available height and width to context in case needed for rendering content (i.e. images)
			if (!ctx.availableWidth) {
				const nColumn = this.getTableCellIndex(refNode);
				ctx.availableWidth = this.scrollData.getAvailableColumnWidth(nColumn);
				ctx.availableHeight = csiImage.getImageSizeMaxHeight(ctx.prefImageSize);
			}

			this.inherited(arguments);

			_.each(classes, function(css) {
				domClass.add(refNode, css);
			});
		},

		refreshHierarchyRow: function(/*DOMNode*/row, /*Boolean*/bForceExpand) {
			if (!row) return null;

			var hs = this.getHierarchyState(row);
			var qr = this.getQueryResultByDomNode(row);

			// hide and delete child rows
			this.collapseRow(row);
			this._deleteChildren(row);

			// redraw row
			var rows = this._refreshRow(row, true);

			// reset store so it will recalculate when expanded
			qr.store = null;

			// expand row if previously expanded
			if (bForceExpand || (hs == csiTable.HS_MINUS)) {
				return this.expandRow(rows[0]);
			}

			return null;
		},

		refreshParentRows: function(/*DOMNode*/childRow, /*Boolean*/bIncludeRow) {
			// summary: refresh all parents of row, including childRow if desired; return refreshed childRow
			var rows = [];
			var rowParent;

			if (bIncludeRow) {
				rowParent = childRow;
				childRow = null;
			} else {
				rowParent = this.getRowParent(childRow);
			}

			while (rowParent) {
				rows.push(rowParent);
				rowParent = this.getRowParent(rowParent);
			}

			var newRows = _.map(rows, function(row) {
				return this._refreshRow(row, true);
			}, this);

			// _refreshRow returns an array of rows, so return first row of first refreshed row if bIncludeRow
			if (!childRow && newRows.length) {
				childRow = newRows[0][0];
			}

			return childRow;
		},

		/**
		 * Remove given query result id from the table
		 * @param {string} id
		 */
		removeResultWithId: function(id) {
			const row = this.getRefNodeById(id);
			if (row) {
				this.deleteRow(row);
			} else {
				// if not rendered, then traverse query results looking for need to remove from the store too
				const qr = this.getQueryResult().removeChildResultWithId(id);
				if (qr) {
					// refresh rows under which result was removed so that group counts are updated
					const row = this.getRefNodeById(qr.id);
					if (row) {
						this.refreshParentRows(row, true);
					}

					// update counts
					this._set("queryTotalCount", this.queryTotalCount - 1);

					if (this.queryDisplayCountTarget != null) {
						this._set("queryDisplayCountTarget", Math.max(this.queryDisplayCountTarget - 1, 0));
					}

					this.set("message");
				}

				// remove from the main store for the widget - when the view is grouped, this store is different
				// from any of the stores on the query results and may or may not exist
				if (this.store) {
					this.store.remove(id);
				}
			}
		},

		/**
		 * Update report rows after current process is done - do not want report row calculations to hold up loading of table
		 */
		scheduleRefreshReportRows() {
			if (this.bReporting) {
				// debounce the call to refresh reports as there are a variety of reasons to refresh and it
				// is difficult to keep this function from being called any number of times
				this._debouncedRefreshReportRows ||= _.debounce(() => this._refreshReportRows(), 500);

				Tracking.addJob(() => this._debouncedRefreshReportRows(), this.getDeferred());
			}
		},

		/**
		 * Replace row with new row(s), which could include merged rows
		 * @param {DOMNode} row
		 * @param {DOMNode[]} newRows
		 */
		_replaceRow(row, newRows) {
			const newRow = newRows[0];
			const oldMergedRows = this.getRowMerges(row, true);
			const lastRowToRemove = oldMergedRows.last() || row;
			const hs = this.getHierarchyState(row);
			const hsNew = this.getHierarchyState(newRow);
			const qr = this.getQueryResultByDomNode(row);
			const rowNext = lastRowToRemove.nextSibling;
			const rowParent = this.getRowParent(row);

			// insert rows will increase display count, so need to offset that here (since not using deleteRow to remove rows)
			if (qr.includeInCount() && !qr.bHidden) {
				this._set("queryDisplayCount", this.queryDisplayCount - 1);
			}

			// replace old row with new row(s)
			html.destroyElement(row);
			_.each(oldMergedRows, html.destroyElement);
			this.insertRows(newRows, rowParent, rowNext);

			// Newly Created group row being re-created during this replace, so we need to update link to the new "Newly Created" group
			if (qr?.bNewlyCreated) {
				this.newlyCreatedNode = newRow;
			}

			if (hs == csiTable.HS_MINUS) {
				// make sure expanded state is same after replacing row
				this.setHierarchyState(newRow, hs);
			} else if ((hsNew === csiTable.HS_NONE) && (hs != hsNew)) {
				// delete children rows (could be actions) if new row does not have children
				this._deleteChildren(row);
			}

			// notify of new page content
			this.publishDomChange();
		},

		/**
		 * Style element so that it is indented properly for given hierarchy level and state
		 * @param {DOMNode} actionElement
		 * @param {number} hl - hierarchy level
		 * @param {string} hs - hierarchy state
		 */
		_setElementHierarchyState: function(actionElement, hl, hs) {
			var icon = (hs === csiTable.HS_PLUS) ? "iconCollapsed" : ((hs == csiTable.HS_MINUS) ? "iconExpanded" : "iconTreeLeaf");
			var ql = this._queryLevels[this.isShowingRootNode() ? 0 : 1];
			if (ql.moveAction) {
				++hl;
			}
			domStyle.set(actionElement, "margin-left", (hl * 0.7) + "em");
			html.setClass(actionElement, "csiAction csiActionIcon tableHierarchy " + icon);
		},

		/**
		 * If node is inside a table cell, then make sure it is scrolled horizontally into view before scrolling vertically
		 * @param {DOMNode} domNode
		 */
		scrollIntoView: function(domNode) {
			if (domNode) {
				const td = html.getParentByTagName(domNode, "td");
				if (td) {
					this.scrollData.scrollHorizontalToCell(td);
				}
			}

			this.inherited(arguments);
		},

		setHierarchyLevel: function(/*DOMNode*/row, /*Number*/hl) {
			// summary: set hierarchy level for row
			csiTable.setRowHierarchyLevel(row, hl);
		},

		setHierarchyState: function(/*DOMNode*/row, /*String?*/hs) {
			// summary: update row styling based on given hierarchy state - see csi/table HS_* constants; root row is always expanded, so hierarchy state does not apply
			if (row) {
				var actionElement = query(".tableHierarchy", row.childNodes[this._nFirstColumn])[0];
				if (actionElement) {
					this._setElementHierarchyState(actionElement, this.getHierarchyLevel(row), hs);
				}

				html.setDataAttribute(row, html.DATA_TABLE_STATE, hs || csiTable.HS_NONE);
			}
		},

		_setHierarchyVisibility: function(/*DOMNode*/element, /*Boolean*/bShow) {
			var row = this.getRefNode(element);
			var self = this;

			if (!this.isRefNode(row, true) || !this.getHierarchyState(row)) {
				return null;
			}

			// change visible image from plus sign to minus sign or vice versa
			var hs = this.getHierarchyState(row);
			var dfd;

			this.setHierarchyState(row, bShow ? csiTable.HS_MINUS : csiTable.HS_PLUS);

			// the next non-merged row would have to be the first child of the row
			var level = this.getHierarchyLevel(row);
			var nextLevel = level + 1;
			var childTest = function(r) {return !self.isMergedRow(r);};

			var child = this.getRefNodeNext(row, false, false, childTest);
			if (child && (this.getHierarchyLevel(child) != nextLevel)) {
				child = null;
			}

			if (bShow) {
				var bChild = false;

				// expand one level of tree
				while (child) {
					var childLevel = this.getHierarchyLevel(child);
					if (childLevel <= level) break;

					if (childLevel == nextLevel) {
						bChild = true;
						this._setRowVisibility(child, true);

						// make sure child row is showing correct image
						if (this.getHierarchyState(child) == csiTable.HS_MINUS) {
							this.setHierarchyState(child, csiTable.HS_PLUS);
						}
					}

					child = child.nextSibling;
				}

				// there are no child rows to show, so generate child rows on demand
				if (!bChild) {
					dfd = this._generateChildren(row, this.getRefNodeNext(row));
				}

				this.publishDomChange();
				return when(dfd);
			}

			// collapse all levels of tree below this one if expanded
			if (hs == csiTable.HS_MINUS) {
				while (child && (this.getHierarchyLevel(child) > level)) {
					this._setRowVisibility(child, false);
					child = child.nextSibling;
				}
			}

			this.publishDomChange();
			return null;
		},

		_setRowVisibility: function(row, bVisible) {
			var qr = this.getQueryResultByDomNode(row);

			if (bVisible == !!qr.bHidden) {
				if (qr.includeInCount()) {
					this.set("queryDisplayCount", this.get("queryDisplayCount") + (bVisible ? 1 : -1));
				}

				qr.bHidden = !bVisible;

				_.each(this.getRowMerges(row), bVisible ? _show : _hide);

				// if the row containing the currently selected attribute is hidden, then reset selection
				if (!bVisible) {
					csiAction.stopNonModalAction(row);
				}
			}
		},

		showSortHeaders: function(/*DOMNode?*/tr) {
			// summary: add sort indicator to specified column - if no row given, then refresh current header row;
			// if row is given, then just apply current sorts to new row
			const {columnData} = this;

			if (!tr) {
				tr = this.getHeadRow();
				html.queryDom([".", CSS_HEADER_ARROW], tr).forEach(e => html.clear(e, true));
			}

			_.each(this.get("sortSettings"), (sortObj, i) => {
				// find column header to match sort specification
				const spec = columnData.getByKey(sortObj.key, true);
				if (!spec) {
					return;
				}

				const span = html.queryDom(["th", html.getDataAttributeSelector(html.DATA_TABLE_SPEC_ID, spec.sid), " .", CSS_HEADER_ARROW], tr, true);
				if (span) {
					new SortIndicator({
						descending: sortObj.descending,
						level: i ? String(i + 1) : ""
					}).placeAt(span);
				}
			});
		},

		sortByHeader: function(/*DOMNode*/th) {
			// summary: sort by given column - if already current sort column, then toggle direction; otherwise init to ascending
			var spec = this.getColumnSpec(th);

			if (!this.canSort() || !spec || !spec.canSort()) {
				return null;
			}

			var sorts = this.get("sortSettings");
			var sortOld = sorts && sorts[0];
			var specOld = sortOld && this.getHeadingSpec(sortOld.key);
			var descending = (specOld && (spec.uniqueId == specOld.uniqueId)) ? !sortOld.descending : false;

			sorts = [{key: spec.uniqueId, descending: descending}];

			// secondary sort is by name if column other than that is being sorted
			if (this.secondarySortByName) {
				var specName = this.getSpec(xml.queryKey(xml.ATTR_NAME));
				if (specName && (specName != spec)) {
					sorts.push({key: specName.uniqueId});
				}
			}

			this.set("sortSettings", sorts);
			this.showSortHeaders();
			this.scrollData.refreshHeader();

			// we want the scroll position to return to top after sorting
			this._scrollPosition = 0;

			return this.redrawData();
		},

		traverseColumn: function(/*Number*/n, /*DOMNode?*/scopeNode, /*function(DOMNode cell)*/f, /*Object?*/scope) {
			// summary: apply function f to cells at index n for entire column, except for footer which only has one cell; function parameters are as follows:
			// 		cell: node at given index for row if not null (can be null for merged rows)
			var queryString = "> tr";

			scope = scope || win.global;

			function traverseFunction(tr) {
				// call function with cell and whether or not cell is at exact index or spans it
				var node = this.getTableCellAtIndex(tr, n);
				if (node) {
					f.call(scope, node);
				}
			}

			if (scopeNode) {
				query(queryString, scopeNode).forEach(traverseFunction, this);
			} else {
				query(queryString, this.headNode).forEach(traverseFunction, this);
				query(queryString, this.bodyNode).forEach(traverseFunction, this);
			}
		},

		traverseDataRows: function(/*Function*/callback, /*Function?*/filterFunc, /*Boolean*/bAllowActions) {
			// summary: traverse all rows in table body (not report based on some filter function
			this.getRefNodes().forEach(function(tr) {
				// check to make sure row has not been removed as part of refreshing another row, and is not merged
				if ((tr.parentNode != this.bodyNode) || this.isMergedRow(tr)) {
					return;
				}

				if (!this.isRefNode(tr, true) && (!bAllowActions || !this.isActionRow(tr))) {
					return;
				}

				if (!filterFunc || filterFunc.call(this, tr)) {
					callback.call(this, tr);
				}
			}, this);
		},

		/**
		 * Show or hide table based on chartHideTable setting
		 */
		updateChartTableVisibility: function() {
			html.setVisible(this.scrollNode, !this.chartHideTable);
		},

		/**
		 * @override
		 * Add merged rows so all matrix rows selected
		 */
		select: function(node, b, extras) {
			if (node) {
				if (node.parentNode) {
					extras = this.getExtraRowsToSelect(node);
				}

				this.inherited(arguments, [node, b, extras]);
			}
		},

		/**
		 * @override
		 * Provide root node for selections
		 */
		unselectAll: function(rootNode) {
			this.inherited(arguments, [this.bodyNode]);
		},

		_selectRow: function(/*DOMNode*/tr, /*Boolean*/bSelected, /*Boolean*/bReset) {
			// summary: (de)select row
			if (!tr) {
				return;
			}

			if (this.isGroupRow(tr) && this.restrictGroupSelection) {
				return;
			}

			var bClearSelections = bReset || !this.allowMultiSelect;
			if (bClearSelections) {
				this.unselectAll();

				if (this._selectionSimulation() && !bSelected) {
					this.onSelectRow(null, false);
				}
			}

			if (bSelected || !bClearSelections) {
				this.select(tr, bSelected);

				if (this._selectionSimulation()) {
					this.onSelectRow(tr, bSelected);
				}
			}
		},

		_selectionSimulation: function() {
			// summary: simulate selection to highlight rows when table is not in selection mode
			return !this.allowsSelections();
		},

		_setDisabledAttr: function(/*Boolean*/disabled) {
			// summary: enable/disable view widgets
			if (this.allowsSelections()) {
				this.disableSelectionWidgets(disabled);
			}

			this.inherited(arguments);
		},

		resetForm: function() {
			// summary: if checkboxes are in matrix cells, then needs extra work - assumption is that all checkboxes off by default
			var view = this._getFormDataWidget();
			var fd = view && view.defView.m_formData;
			if (fd) {
				fd.resetSelections(view.getContext());
			}

			this.inherited(arguments);
		},

		validateForm: function(/*Object*/errorParams) {
			// summary: if selection is required and none exists, then show error
			// NOTE: this is not used when using a React dialog (Go to Dialog)
			var view = this._getFormDataWidget();
			var fd = view && view.defView.m_formData;
			var error = fd && fd.getValidationError();

			if (error) {
				errorParams.suffix = error;
				return false;
			}

			view.invalidNode.set("value", "");
			return true;
		},

		preAction: function(/*Controller*/ac) {
			// summary: close actions drop-down when starting any action
			if (this._selectionSimulation()) {
				// if action happens inside the table - select the row.
				const actionId = ac.m_action.m_id;
				const row = this.getRefNode(ac.refNode || ac.m_element);

				if (row &&
					(row.parentNode == this.bodyNode) &&
					!this.isActionRow(row) &&
					(actionId !== "Dnd") &&
					(!this.isDndAvailable() || (actionId !== this.ACTION_SELECT))
				) {
					// select row being acted upon and clear highlight
					this.highlight();
					this._selectRow(row, true, true);
				}
			}

			this.inherited(arguments);
		},

		postAction: function(/*Controller*/ac, /*Boolean*/bPendingAction) {
			// summary: after any action finishes, look to update report rows
			if (!this.inherited(arguments)) {
				return false;
			}

			if (ac && ac.bDataChanged && this.bReporting) {
				var row = this.getRefNode(ac.refNode) || this.getRefNode(ac.m_element);
				if (row) {
					// mark report row as outdated that pertains to data in action
					const rowParent = this.getRowParent(row);
					const qrParent = this.getQueryResultByDomNode(rowParent);
					this._updateRowReport(qrParent);
				}
			}

			// if action completed successfully, then assume table content may have changed and needs to be resized
			if (ac && ac.endStatus && (ac.getAction().getId() != this.ACTION_SELECT) && !bPendingAction) {
				this.publishDomChange();
			}

			if (!bPendingAction) {
				this._refreshPlugins = false;
			}

			return true;
		},

		onClick: function(/*Event*/e) {
			// this flag can be set if filter is open.
			if (this.disableOnClick) {
				return;
			}

			// handle click event in table to support table row selection
			const {target} = e;
			const bOnHandle = !!target.closest("." + html.CSS_DND_HANDLE);
			let tr;

			function getBodyRow() {
				return target.closest("tbody > ." + CSS_ROW);
			}

			if (this._selectionSimulation()) {
				// determine clicked row
				tr = this._highlightNode || getBodyRow();

				if (tr && !this.isSelected(tr) && (e.metaKey || e.ctrlKey) && bOnHandle) {
					return;
				}

				if (tr && !this.isSelected(tr) && !this.isDndAvailable()) {
					// row will be selected in preAction; reset tr to null so it will not be unselected below
					tr = null;
				}
			} else if (this.enableMassSelect) {
				// if in a mass-selection table, then disable any clicks on body rows that are not in the selection (first) column
				const fd = this.defView.m_formData;
				const closestCell = target.closest("td");
				const closestRow = getBodyRow();

				if (fd.hasSelections() && closestRow && (closestCell !== closestRow.firstChild)) {
					return;
				}
			}

			// invoke action when event is not on dndHandle
			var actionInvoked;

			if (!bOnHandle) {
				actionInvoked = this.inherited(arguments);
			}

			// deselect row on click
			if (tr && this.allowDeselect && (!actionInvoked || (actionInvoked == this.ACTION_SELECT)) && !this.isDndAvailable()) {
				var actEndTime = state.get("lastActionEndTime");
				var currentTime = new Date().getTime();
				// Action is just ended, mean this click ended it - do not deselect the row then
				if ((actionInvoked == this.ACTION_SELECT) || (currentTime - actEndTime > 500)) {
					this._selectRow(tr, false, true);
				}
			}
		},

		/**
		 * @override
		 * Tables must have at least one option
		 */
		onCustomViewAttributeOptions(options) {
			options.requireOne = true;
			this.inherited(arguments);
		},

		/**
		 * Extension point called when a row is inserted into the table body
		 * @param {DOMNode} row
		 */
		onInsertBodyRow: function(row) { },

		/**
		 * Handle keypress event in view
		 * @param {Event} e
		 * @param {Object} params
		 */
		onKey: function(e, params) {
			return this.scrollData.onKey(e);
		},

		/**
		 * @override
		 * Forward the event
		 */
		onKeyRelease: function(e) {
			return this.scrollData.onKeyRelease(e);
		},

		/**
		 * Extension point called when rendering cell with hierarchy / DnD support
		 * @param {DOMNode} row
		 * @param {DOMNode} firstCell
		 */
		onRefreshFirstCell: function(row, firstCell) { },

		/**
		 * Extension point allowing each row to have custom styling
		 * @param {DOMNode} row
		 * @param {QueryResult} qr
		 */
		onRowStyling: function(row, qr) { },

		onSelect: function(/*Context*/ctx, /*String*/url, /*Boolean*/bOn, /*Boolean*/bReplaced) {
			// summary: extension point for view selections
			var fd = this.defView.m_formData;
			if (!fd) {
				return;
			}

			// call base class to highlight rows
			this.inherited(arguments);

			// handle selection of checkboxes
			if (this.allowsSelections()) {
				var refNode = this.getRefNodeByUrl(ctx.getNodeURL()) || this.getRefNodeByUrl(url);
				if (refNode) {
					this.changeSelectionWidget(refNode, bOn);

					if (this.enableMassSelect) {
						this.headingsNode.classList.toggle("csi-table-has-mass-selection", fd.hasSelections());
					}
				}
			}

			// refresh disabled status of action buttons based on selections
			if (!this.get("disabled")) {
				this._setActionButtonsDisabled(false);
			}
		},

		/**
		 * Extension point called when a row is selected or deselected, but only when simulating selections (otherwise onSelect will be called)
		 * @param {DOMNode} tr - if null, then means all rows were deselected (bSelected will be false too)
		 * @param {boolean} bSelected
		 */
		onSelectRow: function(tr, bSelected) { },

		/**
		 * Start loading more results given domNode for row with more action
		 * @param {DOMNode} [domNode]
		 * @param {number} [count]
		 * @return {Promise}
		 */
		queryMore: function(domNode, count) {
			return this._load(this._queryMore, domNode, true, count);
		},

		/**
		 * View all main level results that are not yet viewed because of More... links; this function will not expand any rows that are collapsed
		 * @return {Promise}
		 */
		queryMoreForMainLevel: function() {
			return this._load(this._queryMoreForMainLevel);
		},

		/**
		 * if not all results are displayed but table removed scroll bar - get more results
		 */
		queryMoreIfNeeded: function() {
			// when user has no rights to see the table, then queryDisplayCount == 0
			let {queryDisplayCount, queryTotalCount, tableScrollNode, errorOnLoad} = this;
			if (queryDisplayCount && (queryDisplayCount < queryTotalCount) && !html.isScrollingVertically(tableScrollNode) && !errorOnLoad && !this.isSuspended()) {
				this.queryMoreToTarget();
			}
		},

		/**
		 * Incrementally load more results if current display count is less than the target; if force is true, then
		 * force loading of all remaining up to target; otherwise only load based on scroll position
		 * @param {boolean} force
		 * @return {Deferred}
		 */
		queryMoreToTarget: function(force) {
			const {
				bodyNode,
				incrementalQuerySize,
				naturalScrollNode,
				queryDisplayCount,
				queryDisplayCountTarget,
				queryTotalCount,
				tableScrollNode,
			} = this;

			const limit = this.getQueryLimitIfReached();
			const total = limit || queryTotalCount;
			const target = Math.min(queryDisplayCountTarget, total);

			if (!this.isLoadingView() && incrementalQuerySize && (queryDisplayCount < target)) {
				let count;

				if (force) {
					// load all remaining results up to target
					count = target - queryDisplayCount;
				} else {
					// start loading more content if scrolled within the last 25% of the visible view (up to a maximum of 200px);
					// we only want to load more when user reaches bottom of the table, but in a table that is short (close to 200px) it
					// doesn't make sense to incrementally load when there is still at least one more "page" of rows to view
					const scrollNode = this.scrollData.useNaturalHorizontalScrolling() ? naturalScrollNode : tableScrollNode;
					const full = scrollNode.scrollHeight;
					const visible = scrollNode.offsetHeight;
					const partial = scrollNode.scrollTop + visible;
					const threshold = Math.min(200, visible * 0.25);
					force = (full - partial) < threshold;
					count = incrementalQuerySize;
				}

				if (force) {
					const lastRow = bodyNode.lastChild;
					const qr = this.getQueryResultByDomNode(lastRow);
					this.tableShowWait(true);
					return this.queryMore(qr.bAction && qr.parentResult.isRoot() ? lastRow : null, count);
				}
			}

			return null;
		},

		_queryMoreForMainLevel: function() {
			// summary: view all main level results that are not yet viewed because of More... links; this function will not expand any rows that are collapsed
			var urls = [];
			var store;
			var results = _.filter(this.getQueryResults(), function(qr) {
				var qrParent = qr.parentResult;
				if (qr.bAction && qrParent.hasMoreChildren()) {
					store = qrParent.getStore();
					urls = urls.concat(store.getResults().slice(qrParent.getChildDisplayCount()));
					return true;
				}

				return false;
			});

			// nothing more to do if no store found
			if (!store) {
				this._endView();
				return;
			}

			// reset query size to 0 to query for all remaining results - will be restored later
			var querySize = this.querySize;

			this.querySize = 0;

			// get "more" nodes in one request rather than one per group, then expand
			var request = store.createNodesRequest && store.createNodesRequest(urls);

			return this.when(
				when(request && request.go(), res => {
					store.cachingStore.cacheResponse(res);

					var dfds = [];

					_.each(results, (qr) => {
						var domNode = this.getRefNodeById(qr.id);
						if (domNode) {
							dfds.push(this._queryMore(domNode));
						}
					});

					return all(dfds);
				})
					.then(() => this._autoExpand())
					.then(() => this._endView())
			).then(() => {
				this.querySize = querySize;
			});
		},

		/**
		 * Query more results given domNode for row with more action
		 * @param {DOMNode} [domNode]
		 * @param {boolean} [bExpand] - if true, then also expand and complete view operation
		 * @param {number} [count] - if specified, then overrides default incremental count to load
		 * @return {Deferred}
		 */
		_queryMore: function(domNode, bExpand, count) {
			var row = this.getRefNode(domNode);
			var rowParent = this.getRowParent(row);
			var rowNext = row ? row.nextSibling : null;
			var qr = this.getQueryResultByDomNode(rowParent);
			var queryOptions = this.getQueryResultOptions(qr, true);

			if (count) {
				queryOptions.count = count;
			}

			var promise = this._query(qr, queryOptions)
				.then(
					results => {
						this.onViewResults(qr, results);

						// capture hierarchy state so that auto-expand below will expand newly added rows to default expand level without affecting existing rows
						if (bExpand) {
							this.captureHierarchyState();
						}

						// remove all action rows - they will be put back when generating next set of results
						while (this.isActionRow(row) && (this.getRowParent(row) == rowParent)) {
							row = row.previousSibling;
							this.deleteRow(row.nextSibling, rowParent);
						}

						return this._buildViewResults(qr, rowParent, rowNext, results);
					},
					err => {
						return this._buildViewResults(qr, rowParent, rowNext);
					}
				);

			if (bExpand) {
				promise = promise
					.then(() => this._autoExpand())
					.then(() => {
						this._endView();
					});
			}

			return this.when(promise);
		},

		redraw: function() {
			// summary: when redrawing, expand what was previously expanded
			this.captureHierarchyState();
			return this.inherited(arguments);
		},

		redrawCharts: function() {
			// summary: when redrawing, expand what was previously expanded
			return this.getPlugin("charts").renderCharts();
		},

		redrawData: function() {
			// summary: when redrawing, expand what was previously expanded
			this.captureHierarchyState();
			return this.inherited(arguments);
		},

		/**
		 * @override
		 * Clear query levels so column specs will be resolved, and then redraw
		 */
		redrawSpecs: function() {
			this._resetLevels();
			return this.redraw();
		},

		/**
		 * Select all checkboxes in view; ensure that to trigger loading of all results
		 * that would be loaded on demand when scrolling
		 * @param {boolean} checked
		 */
		selectAll: function(checked) {
			csiAction.invoke(this, null, ACTION_SELECT_ALL, {
				actionValue: checked
			});
		},

		/**
		 * Reset fixed column widths to defaults
		 */
		resetFixedColumnWidths: function() {
			csiAction.invoke(this, null, ACTION_RESET_COLUMN_WIDTHS, {
				actionValue: {
					fixedColumnWidths: {}
				}
			});
		},

		/**
		 * Start action to update column width
		 * @param {number} column
		 * @param {number} width
		 */
		setFixedColumnWidth: function(column, width) {
			const {fixedColumnWidths} = this;
			const spec = this.columnData.getByIndex(column, true);

			csiAction.invoke(this, null, ACTION_COLUMN_RESIZE, {
				actionValue: {
					fixedColumnWidths: Object.assign({}, fixedColumnWidths, {
						[spec.getColumnWidthKey()]: width
					})
				}
			})
		},

		/**
		 * @override
		 * Toggle table suspension by hiding/showing body and disabling/enabling view queries
		 */
		suspendView(suspended) {
			if (suspended) {
				this.set("suspended", true);
				// refreshOnSuspend should already be false
			} else {
				const bUpdated = this.get("refreshOnSuspend");
				this.set("refreshOnSuspend", false);
				this.set("suspended", false);

				// if view query parameters (sorting, filtering) have changed then refresh
				if (bUpdated) {
					this.tableShowWait(true);
					return this.redrawData();
				}

				// show body as it was since nothing changed
				this.set("message");
			}

			this.updatePlaceholder();
		},

		/**
		 * Add row to memory table and apply CSS for first/last columns
		 * @param {DOMNode} row
		 * @param {DOMNode} [rowMergedWith] - row that given row is merged with (if any)
		 */
		_addRowToMemoryTable: function(row, rowMergedWith) {
			const memoryTable = this.memoryTable;
			memoryTable.add(row, rowMergedWith);
			memoryTable.addCellClass(row, 0, CSS_FIRST_COLUMN);
		},

		/**
		 * Build body of table
		 * @return {Promise<void> | undefined}
		 */
		_buildBody() {
			const node = this.getViewNode();

			// reset scroll position during full redraw
			this.tableScrollNode.scrollTop = 0;

			if (this.isShowingRootNode()) {
				this._deleteChildren();

				if (node) {
					// add root row to table
					const qr = this.getQueryResult();
					qr.hl = 0;
					this.insertRows(this._generateResultRow(qr));

					// expand root row if there is hierarchy - otherwise remove table functionality only available with relationships
					var row = this.getRootRow();

					if (this.getHierarchyState(row)) {
						return this._setHierarchyVisibility(row, true);
					} else if (this.footNode && !this.bHierarchy) {
						this._destroyFooter();
					}
				}
			} else if (node) {
				const qr = this.getQueryResult();
				qr.hl = -1;
				return this._generateChildren();
			} else {
				this._deleteChildren();
			}
		},

		/**
		 * Create comparison data if so configured
		 * @return {Promise<void>}
		 */
		async _buildCompareData() {
			const {allowComparison, bPopup, compareData, isComparing} = this;
			const {pageStore} = rootStore;
			const isComparisonView = isComparing && !compareData;
			for (const key in this._queryResultMap) {
				this._queryResultMap[key].comparisonResult = undefined;
			}

			if (pageStore && pageStore.isCompareMode && allowComparison && !isComparisonView && !bPopup) {
				const data = await createCompareData(this, pageStore.compareRevisionNode);
				this.set("compareData", data);
				this.set("isComparing", !!data);
			} else if (compareData) {
				this.set("compareData", null);
				this.set("isComparing", false);
			}
		},

		_buildContentMessage: function(/*DOMNode?*/contentNode, /*String?*/message) {
			// summary: hide table when message shown
			html.setVisible(this.scrollNode, false);
			this.inherited(arguments);
		},

		/**
		 * Calculate fixed column widths
		 */
		_fixColumnWidths: function() {
			if (this.dynamicColumnWidths) {
				return;
			}

			// find all column widths based on provided map - initialize widths to 1 for default border width
			const nColumns = this.numberOfColumns;
			const headerWidths = Array(nColumns).fill(0);
			const widthsByKey = this.fixedColumnWidths || {};
			let bodyWidth = 0;

			this.columnData.traverse(true, (c, sc, i, n) => {
				const spec = sc || c;
				const key = spec.getColumnWidthKey();
				let w = this._getSpecColumnWidth(spec, widthsByKey[key], n === this._nFirstColumn);

				if (sc && sc.firstInMatrix) {
					// add extra pixel for matrix border
					++w;

					// add extra pixel to end of matrix border
					const nLast = n + c.subs.length;

					if (nLast < n) {
						++headerWidths[nLast];
						++bodyWidth;
					}
				}

				headerWidths[n] += w;
				bodyWidth += headerWidths[n];
			});

			if (state.get("print")) {
				// convert widths from px to em so print scaling will work
				bodyWidth = 0;

				const em = html.EM;
				const ratio = html.pixelTo(em, this.domNode);
				const emWidths = _.map(headerWidths, w => {
					w = util.truncate(w * ratio, 0.01);
					bodyWidth += w;
					return w + em;
				});

				// when printing, fix column widths as well (but for now in single table rather than separate tables for header and body)
				// FIX: eventually we could try adapting print algorithm based on fixed-width table without freezing columns;
				// that would save doing much of the same work in the print algorithm after the table is rendered
				$(this.tableContainerNode).addClass("csi-view-table-fixed");
				csiTable.fixTableWidths(this.getHeadTableNode(), bodyWidth + em, emWidths);
			} else {
				// set fixed column widths
				this.scrollData.setHeaderWidths(headerWidths, bodyWidth);
			}
		},

		/**
		 * Return default width for column with given spec; the width of image columns is determined by view options rather
		 * than being based purely on its type and format
		 * @param {Spec} spec
		 * @param {number} fixedWidth
		 * @param {boolean} isFirstColumn
		 * @return {number}
		 */
		_getSpecColumnWidth: function(spec, fixedWidth, isFirstColumn) {
			if (!fixedWidth) {
				fixedWidth = spec.isImage() ? (this._getTableImageHeight() + 2 * csiTable.IMAGE_CELL_PADDING + 1) : spec.getColumnWidth();

				// increase width of first column by width required for hierarchy levels - the icon takes 16px, and then an extra 10px (or so) margin per level
				if (isFirstColumn) {
					const hierarchyLevels = this.getNumberOfResultLevels() - 1;

					if (hierarchyLevels) {
						fixedWidth += 16 + hierarchyLevels * 10;
					}
				}
			}

			return fixedWidth;
		},

		/**
		 * Return configured height for images in table based on chosen image size; this does not apply to images in header, or images in cells with other content
		 * @return {number}
		 */
		_getTableImageHeight: function() {
			// if image size is Icon, then just return smallest image height
			return this.tableImageHeight ||
				csiImage.getImageSizeMaxHeight(this.imageSize) ||
				csiImage.getImageSizeMaxHeight(reflection.IMAGE_SIZE_SMALLEST);
		},

		/**
		 * Determine what columns exist in the table
		 * @return {Deferred}
		 */
		_buildColumns: function() {
			const ctx = this.getContext();
			const dfds = [];

			this._resolveColumns();

			// temporarily hide table - when switching from one custom view that does not have charts to another that does,
			// we do not want to see the old table while the charts are appearing; this table will be shown again
			// in _buildTable
			html.setVisible(this.tableContainerNode, false);

			this.onBuildColumns(ctx, dfds);

			return all(dfds);
		},

		/**
		 * Generate rows for table header and insert into DOM
		 */
		_buildHead: function() {
			const {headNode} = this;

			// generate rows
			const rows = this._buildHeadRows();

			// add header actions/styling to rows - must do after adding rows to memory table to support getTableCellAtIndex
			if (this.canSort() && this.getViewNode()) {
				this.columnData.traverse(true, function(c, sc, nSpec, n) {
					if (!sc && c.canSort()) {
						var th1 = this.getTableCellAtIndex(rows[0], n);
						domClass.add(th1, "csi-table-sort");
					}
				}, this);
			}

			// freeze columns in rows and add to table
			this.scrollData.fixTableRows(rows, true);

			_.each(rows, function(row) {
				this.setHierarchyLevel(row, 0);
				headNode.appendChild(row);
			}, this);
		},

		/**
		 * Render and return rows for header
		 * @return {DOMNode[]}
		 */
		_buildHeadRows: function() {
			var cd = this.columnData;
			var ctx = this.getContext(csi.EMPTY_URL);
			var nCheckboxColumn = (this.allowsSelections() && !ctx.fd.m_bSelectOne) ? 0 : -1;
			var headerRow = this._generateTableRow();
			var subheaderRow;
			var rows = [headerRow];
			let bSubsToRender = false;

			// any images in header should be smallest size
			ctx.prefImageSize = reflection.IMAGE_SIZE_SMALLEST;

			// determine if there is a sub-header row to render
			if (cd.bSubCells) {
				cd.traverse(false, (c) => {
					bSubsToRender |= c.hasSubheadersToRender();
				});

				if (bSubsToRender) {
					subheaderRow = this._generateTableRow(["csi-table-subheaders"]);
					rows.push(subheaderRow);
					html.setDataAttribute(subheaderRow, html.DATA_TABLE_MERGED, true);

					if (!ctx.bExcel) {
						html.setElementAction(subheaderRow, ACTION_HEADER);
					}
				}
			}

			this.hasSubheaderRow = bSubsToRender;

			// create first header row
			cd.traverse(false, function(c, sc, nSpec, n) {
				const th = this.generateHeaderCell(ctx, c, null, headerRow, c.headerParams);
				let subs = c.subs;
				let nSubs = subs ? subs.length : 0;

				if (ctx.bExcel) {
					// export the width as well
					th.style.width = this.scrollData.getFixedColumnWidth(n) + html.PX;

					// filter out sub-headings that should not be exported
					// TODO: not currently handling case where matrix could disappear entirely (very rare case in my opinion)
					if (subheaderRow && nSubs) {
						subs = _.filter(subs, function(sub) {
							const css = sub.heading.m_class;
							return !css || !_.contains(css.split(" "), html.CSS_NO_EXPORT);
						});

						th.colSpan = nSubs = subs.length;
					}
				} else {
					// store column index and add action for cells which could be to sort or freeze the column
					html.setDataAttribute(th, html.DATA_TABLE_SPEC_ID, c.sid);

					if (_.isNumber(c.index)) {
						csiTable.setHeaderSpecIndex(th, c.index);
					}

					if (subheaderRow && nSubs) {
						csiTable.setMatrixHeader(th);
					}
				}

				if (n == nCheckboxColumn) {
					// special treatment for selection column
					if (!ctx.bExcel) {
						this.generateSelectionHeaderCheckbox(th);
					}
				}

				if (subheaderRow) {
					if (nSubs) {
						// add to sub-header row
						subs.forEach(sc => {
							const sh = this.generateHeaderCell(ctx, c, sc, subheaderRow, sc.headerParams);
							html.setDataAttribute(sh, html.DATA_TABLE_SPEC_ID, sc.sid);
							html.setDataAttribute(sh, html.DATA_TABLE_SPAN_SPEC_ID, c.sid);
						});
					} else {
						// normal header spans both rows
						th.rowSpan = 2;
					}
				}
			}, this);

			if (!ctx.bExcel) {
				html.setElementAction(headerRow, ACTION_HEADER);

				// add sort indicators for columns being sorted
				this.showSortHeaders(headerRow);
			}

			// show dynamic-column header row based on configuration if necessary
			rows = this._buildColumnNodeHeaderRow(ctx, rows);

			// generate filter row if already there - if no valid columns to filter, then remove
			this.onBuildHead(ctx, rows);

			// lastly, if selecting single node and it is not required, then render extra header row for radio button to select nothing
			if (this.allowsSelections() && ctx.fd.m_bSelectOne && !ctx.fd.m_bRequireOne && !ctx.bExcel) {
				const selectionRow = this._generateTableRow(["csi-table-selection-radio"]);
				rows.push(selectionRow);

				cd.traverse(true, function(c, sc, nSpec, n) {
					const th = this.generateHeaderCell(ctx, c, sc, selectionRow);

					if (!n) {
						this.generateSelectionHeaderRadioButton(ctx, th);
					} else if (n == 1) {
						this._onFirstCell(ctx, null, th);
						html.create("span", {innerText: locale.resource("SelectNone")}, th);
					}
				}, this);

				this.enableRefNodeHighlighting(selectionRow);
				this.setRefNodeId(selectionRow, csi.EMPTY_URL);
				html.setElementContext(selectionRow, csi.EMPTY_URL);
			}

			// add row to memory table - do so before adding extra header as that goes beyond max number of columns
			rows.forEach(row => {
				this._addRowToMemoryTable(row, (row === subheaderRow) ? headerRow : null);
			});

			// add extra header cell for header to go above body scrollbar
			if (!ctx.bExcel && !this.hideScrollbar) {
				rows.forEach(row => {
					if (row !== subheaderRow) {
						html.create("th", {
							rowSpan: (subheaderRow && (row === headerRow)) ? 2 : 1
						}, row);
					}
				});
			}

			return rows;
		},

		/**
		 * Create a new row in header for ColumnNodeHeading, 
		 * an optional Heading to render in table header to show extra information about matrix column node
		 * Return the newly created rows of the header including the newly created ColumnNodeHeading
		 * @param {Context} ctx
		 * @param {HTMLElement[]} rows
		 * @return {HTMLElement[]}
		 */
		_buildColumnNodeHeaderRow: function(ctx, rows) {
			var cd = this.columnData;
			var cnh = this.columnNodeSpec?.heading;
			if (cnh && (!cnh._rca || cnh._rca.isEnabled)) {
				const columnRow = this._generateTableRow(["csi-table-node-header"]);
				rows.push(columnRow);
				cd.traverse(false, function(c, sc, nSpec, n) {
					var th = html.create("th", null, columnRow);
					if (c.subs) {
						th.colSpan = c.subs.length;
					}
					if (c.columnUrl) {
						// render heading for column node - assumption is that there will not be two such matrices in the same view
						ctx.setNodeURL(c.columnUrl);
						ctx.applyNodeContext(th);
						csiHeading.toHtml(ctx, cnh, th);
						// make sure to add matrix column CSS
						var params = c.headerParams;
						if (params && params.className) {
							domClass.add(th, params.className);
						}
					} else {
						def.processClass(c.heading, th, c.css);
						if (n == this._nFirstColumn) {
							html.setInnerText(th, cnh.getLabel());
						}
					}
				}, this);
				ctx.setNodeURL(csi.EMPTY_URL);
			}
			return rows;
		},

		_buildTable: function() {
			// summary: build table header/footer
			const bPrint = state.get("print");
			const qr = this.getQueryResult();

			if (bPrint) {
				// reset scrolling table now when other DOM changes are occurring to improve performance
				csiTable.setNumberOfFrozenColumns(this.domNode, this.printFrozenColumnsForEachPage ? this.tableFreezeColumns : 0);
				csiTable.setResultLevel(this.domNode, this.getPageLevel());
			} else if (this.getAutoExpandLevel()) {
				// save scroll position if table needs to be auto-expanded
				this.saveScrollPosition();
			}

			// reset "Newly Created" group row - the DOM is deleted below
			this.newlyCreatedNode = null;

			// clear header and body and make visible again;
			// clear body in one shot if the map of query results has already been reset; otherwise, need to remove DOM and results from map
			html.destroyChildren(this.headNode);

			if (_.size(this._queryResultMap) <= 1) {
				html.destroyChildren(this.bodyNode);
			} else {
				this._deleteChildren();
			}

			html.setVisible(this.tableContainerNode, true);

			// hide footer if so configured
			this.domNode.classList.toggle("csi-table-no-footer", !!this.hideFooter);

			// create memory table
			this.memoryTable = new MemoryTable(this.numberOfColumns, this.numberOfDataColumns);

			// fix column widths (create colgroup) before adding any rows
			this._fixColumnWidths();

			// resolve view definition and render table header
			this._buildHead();

			// handle table footer - if level to query, and either not printing or table is not slim, then allow footer
			if (this.footNode && qr.level.childLevel && (!this.defView.isSlim(true) || !state.get("print"))) {
				this.footContentNode.parentNode.colSpan = this.numberOfColumns;
			} else {
				this._destroyFooter();
			}

			// fix table heights if fixing column widths now that header height can be calculated
			this.scrollData.fixTableHeights();

			this._setHeaderVisibility();
		},

		_buildView: function() {
			// summary: search for dynamic columns before building table

			// TODO: eventually this should be handled at csi/view/Widget level, but need to reorganize the code
			if (this.get("contentMessage")) {
				this.inherited(arguments);
				return;
			}

			var ctx = this.getContext(this.urlRoot);
			var dfds = [];

			// reset any effects of _buildContentMessage
			html.setVisible(this.scrollNode, true);
			domConstruct.empty(this.contentNode);

			// create query levels for table
			this._createQueryLevels();
			this.set("queryTotalCount", null);
			this.set("queryDisplayCountTarget", null);
			this.set("queryDisplayCount", null);

			// call extension point to add custom requests before building table
			this.onBuildDeferreds(ctx, dfds);

			// make requests needed for matrices
			if (ctx.getNode()) {
				// traverse sub-headings because it is possible for a Index matrix to be used within another matrix
				this.attributeSpecs.traverse(false, ({heading, subs}) => {
					heading.getMatrixProcessor()?.addDeferred(ctx, heading, dfds);

					subs?.forEach(({heading}) => {
						heading.getMatrixProcessor()?.addDeferred(ctx, heading, dfds);
					});
				});
			}

			// TODO: move to _buildHead?
			if (state.get("print") && !this.printAutoScale && this.printFontScale) {
				var sizeval = this.printFontScale + "%";

				// when printing, there will only be one table, so can use getHeadTableNode or getBodyTableNode
				$(this.getHeadTableNode()).css("font-size", sizeval);
			}

			this.when(all(dfds))
				.then(() => this._buildCompareData())
				.then(() => this._buildColumns())
				.then(() => this._buildTable())
				.then(() => this._buildBody())
				.then(() => this._postView());
		},

		_buildViewResults: function(/*QueryResult*/qrParent, /*DOMNode*/rowParent, /*DOMNode*/rowNext, /*QueryResults?*/results, /*Boolean?*/bRefresh) {
			// summary: update table with results from query
			const {compareData, isComparing} = this;
			var dfd = new Deferred();
			var bIncremental = !this.dynamicColumnWidths;
			var bRefreshAll = bRefresh && !rowParent && !bIncremental;
			var bPreSelect = this.selectWithHierarchy && this.isSelected(rowParent);

			const generateNoRows = () => {
				// remove plus/minus sign
				this.setHierarchyState(rowParent);
			}

			const preSelectRows = rows => {
				if (bPreSelect) {
					rows.forEach(r => {
						this.select(r, true);
					});
				}
			};

			if (bRefresh) {
				// on refresh, reset display count target if loading results incrementally; if querySize is 0 (All), then this will be initialized to the query total
				if (this.incrementalQuerySize && this.querySize) {
					this._set("queryDisplayCountTarget", this.querySize);
				}

				// remove previous results - do so before report results are stored (otherwise they will be immediately reset)
				this._deleteChildren(rowParent, bRefreshAll);
			}

			if (results) {

				// generate row for each node - use timeout after configured number of rows so that browser will not hang
				var ctx = qrParent.getChildContext();
				var report = qrParent.getReport();
				var rows = [];
				var err;

				// all dependency paths should have been queried as part of request for results, so do not force new query when
				// pushing path on the context; this will fix individual queries for nodes to which the user does not have access
				ctx.bPathsQueried = true;

				csi.forEachWithTimeout(
					results,
					this.nRowsPerTimeout,
					function(group) {
						// results will normally be empty if suspended except when grouping
						try {
							if (!err && !this.isSuspended() && this.isValidView()) {
								var newRows = this.generateRow(group, qrParent, ctx);

								rows = rows.concat(newRows);

								if (bIncremental) {
									// ensure that parent row is still in table - if not, then find again
									if (rowParent && !rowParent.parentNode) {
										rowParent = this.getRefNodeById(qrParent.id);
									}

									this.setHierarchyState(rowParent, csiTable.HS_MINUS);
									this.insertRows(newRows, rowParent, rowNext);
									preSelectRows(newRows);
								}
							}
						} catch (ex) {
							err = ex;
						}
					},
					null,
					function() {
						let actionRow;
						let rowsToInsert = bIncremental ? [] : _.clone(rows);
						let row;

						// find results to add from comparison view, if any
						let comparisonResults = compareData ? compareData.findComparisonOnlyResults(qrParent) : [];

						// clear body only after rendering all rows, but before inserting into body; this reduces the time the user sees an empty table while refreshing
						if (bRefreshAll) {
							this._deleteChildren(rowParent, false, true);
						}

						if (err || !this.isValidView()) {
							dfd.reject(err);
						} else {
							// FIX: move to action controller?
							if (this.defView.m_formData) {
								this.defView.m_formData.bPreselectAll = false;
							}

							// ensure that parent row is still in table - if not, then find again
							if (rowParent && !rowParent.parentNode) {
								rowParent = this.getRefNodeById(qrParent.id);
							}

							if (!this.isSuspended()) {
								if (bRefresh &&
									qrParent.isRoot() &&
									!rows.length &&
									!isComparing &&
									this.hasEmptyQuery()
								) {
									// if no results but main level has actions, then ungroup table and render action row;
									// do not do when comparing as structure of the two tables must be the same
									this._removeGroupLevels();
								} else if (qrParent.isGroup() && !qrParent.containsGroups()) {
									// if expanding leaf group node, then refresh if result count has changed from target count;
									// targetCount is only filled if QueryByURL with known URLs as in case of expanding a group
									const {targetCount} = results;

									if (targetCount && (targetCount != results.length)) {
										rowParent = this.refreshParentRows(rowParent, true);
									}
								}

								// generate row for actions if view not suspended
								if (row = this.generateActionRow(qrParent)) {
									actionRow = row;
									rows.push(row);
									rowsToInsert.push(row);

									if (bIncremental) {
										this.insertRows(rowsToInsert, rowParent, rowNext);
										rowsToInsert = [];
									}
								}
							}

							// there is content to insert if there are rows from current view or rows from comparison view
							const nRows = rows.length;
							let bReport = false;

							if (nRows || comparisonResults.length) {
								this.setHierarchyState(rowParent, csiTable.HS_MINUS);

								// generate report if level so configured, but only if rows are from current view
								if (bRefresh && report && !qrParent.bReport && nRows) {
									if (report.supportsGroups || !qrParent.containsGroups()) {
										// rows are added incrementally with fixed column widths, so need to insert before first new result row
										// FIX: when switching to all fixed column width tables, generate this row before results rows rather than after
										if (bIncremental && rows.length) {
											rowNext = rows[0];
										}

										row = this._generateReportRow(report, qrParent, null, true);
										rows.unshift(row);
										rowsToInsert.unshift(row);
										bReport = true;
									}
								}

								// insert new body content
								this.insertRows(rowsToInsert, rowParent, rowNext);

								// add any results from comparison view after last result row; only insert report rows from comparison
								// view if no rows from current view
								for (const comparisonResult of comparisonResults) {
									if (!bReport || !comparisonResult.isSiblingReport()) {
										this.copyComparisonResult(comparisonResult, rowParent, actionRow || rowNext);
									}
								}
							} else {
								generateNoRows();
							}

							// wait for all images to load when printing
							if (ctx.bPrint) {
								waitForImagesToLoad().then(
									function() {
										dfd.resolve(rows);
									},
									function(err) {
										dfd.reject(err);
									}
								);
							} else {
								dfd.resolve(rows);
							}
						}
					},
					this
				);
			} else {
				generateNoRows();
				dfd.resolve([]);
			}

			return dfd;
		},

		/**
		 * @override
		 * Migrate matrix keys to new grouped structure
		 */
		_migrateAndValidateCustomViewValues(version, values) {
			const {specBuilder} = this;

			if (version < CustomViewVersion.V791) {
				migrateTableKeysV791(values, specBuilder);

				// ensure image size is set to custom if table has custom image height
				if (values.tableImageHeight) {
					values.imageSize = IMAGE_SIZE_CUSTOM;
				}
			}

			// TODO: attributeKeys - remove reportSettings items if key not in attributeKeys

			this.inherited(arguments);
		},

		/**
		 * Return true if scrolling is needed; no need to resize if always full height and no footer (to display horizontal scrollbar)
		 * @return {boolean}
		 */
		_shouldResize: function() {
			return !this.forceFullHeight || !this.hideFooter;
		},

		/**
		 * Protect against the store being created too early
		 */
		_createMainStore: function() {
			if (this.columnData) {
				this.inherited(arguments);
			}
		},

		/**
		 * Destroy table footer
		 */
		_destroyFooter: function() {
			if (this.footNode) {
				html.destroyElement(this.footNode);
				delete this.footNode;
				delete this.footContentNode;
				delete this.messageNode;
				delete this.messageRightNode;
				delete this.scrollData.frozenSlider;
			}
		},

		_setHeaderVisibility: function() {
			if (this.hideHeader) {
				domClass.add(this.tableContainerNode, "csi-table-no-header");
			}
		},

		_getExpandState: function() {
			// summary: return list of ids that are currently expanded
			var obj = {};

			this.getRefNodesQuery(null, csiTable.HS_MINUS, true).forEach(function(refNode) {
				obj[this.getRefNodeId(refNode)] = true;
			}, this);

			this.getRefNodesQuery(null, csiTable.HS_PLUS, true).forEach(function(refNode) {
				obj[this.getRefNodeId(refNode)] = false;
			}, this);

			return obj;
		},

		/**
		 * Return Spec to be used for rendering group names
		 * @param {Partial<Spec>} [params]
		 * @return {Spec}
		 */
		_getGroupLevelSpec(params) {
			const {groupRendererClass, specBuilder, _groupLevelSpec} = this;

			if (_groupLevelSpec) {
				return _groupLevelSpec;
			}

			const key = new Key({
				id: xml.ATTR_NAME,
				name: GROUP_HEADING_KEY,
			});

			const spec = this._groupLevelSpec = specBuilder.getSpecForKey(key, params);
			spec.heading.classRenderer = groupRendererClass;
			spec.heading.m_link = null;
			spec.heading.m_right = xml.RIGHT_NONE;
			return spec;
		},

		/**
		* Return the image size based on view
		* @return {string} -  the image size
		*/
		getDefaultImageSize: function() {
			return this.bPopup ? reflection.IMAGE_SIZE_ICON : this.defView.defaultImageSize;
		},

		/**
		 * @override
		 * Fixed column width tables do not need to react to changes to the DOM, except in popup dialogs
		 */
		_onChangeDOM: function() {
			// FIXED_WIDTH: Do we need to do this if in a popup, or does new dialog positioning remove need for this?
			// Looks like the answer is yes as the New action button in the toolbar does not appear until later,
			// so it's safer to keep it here for now
			if (this.bPopup || this.dynamicColumnWidths) {
				this.inherited(arguments);
			}
		},

		_onChangeQueryLevels: function(/*QueryLevel[]*/queryLevels) {
			// summary: set view as hierarchical if more than 2 levels displayed
			const nLevels = this.getNumberOfResultLevels(queryLevels);
			this.bHierarchy = (nLevels >= 2);
		},

		_onCreateQueryLevels: function(/*QueryLevel[]*/queryLevels) {
			// summary: finish configuration of query levels

			// unset bConfigurable for table-spanning relationships because they do not need filters, reports, etc.
			_.each(queryLevels, function(ql) {
				if (csiTable.isTableSpannerRelationship(ql._rel)) {
					ql.bConfigurable = false;
				}
			});

			// add levels for grouping
			if (this.hasGroups()) {
				let qlLast = queryLevels[0];
				const qlMain = queryLevels[1];
				const {parentAttribute, specBuilder, type} = qlMain;

				const groupSettings = this.get("groupSettings");
				const {key1, key2, expand1, expand2} = groupSettings;
				const settings = [];
				if (key1) {
					settings.push({key: key1, expand: expand1});
				}

				if (key2) {
					settings.push({key: key2, expand: expand2});
				}

				_.each(settings, function(obj, i) {
					const groupSpec = this.getHeadingSpec(obj.key);
					if (groupSpec) {
						const ql = this._createQueryLevel({
							bAutoExpand: obj.expand,
							bReadOnly: true,
							bResults: true,
							childLevel: qlMain,
							css: CSS_GROUP,
							groupSpec,
							id: "g" + (i + 1),
							parentAttribute,
							qm: this,
							specBuilder,
							type,
						});

						qlLast.childLevel = ql;
						qlLast = ql;
						queryLevels.splice(i + 1, 0, ql);

						this.onCreateQueryLevel(ql);
					} else {
						console.error("csi/table/Widget#_onCreateQueryLevels - grouping spec not found");
					}
				}, this);
			}

			// add levels for column matrices that support filtering if custom views are allowed
			if (this.supportsCustomViews()) {
				const {configuredMatrixParamsMap} = this.specBuilder;

				for (const key in configuredMatrixParamsMap) {
					const ql = this.createMatrixQueryLevel(key);
					if (ql) {
						queryLevels.push(ql);
					}
				}
			}

			this._onChangeQueryLevels(queryLevels);
		},

		/**
		 * In some cases it is possible to issue a single query to get children for all results, rather than a query for each.
		 * @param {NodeList} nl
		 * @return {Deferred}
		 */
		_onExpandLevel: function(nl) {
			var childUrls;
			var qr;
			var querySize = this.get("querySize");
			var store;
			var urls = [];

			nl.forEach(function(tr) {
				qr = this.getQueryResultByDomNode(tr);
				childUrls = qr.getGroupUrls() || qr.getResultPathUrls();

				if (childUrls) {
					store = store || qr.getStore();

					if (querySize) {
						childUrls = childUrls.slice(0, querySize);
					}

					urls.push(childUrls);
				}
			}, this);

			if (store) {
				var request = store.createNodesRequest?.(_.flatten(urls));
				if (request) {
					return request.go().then(function(res) {
						store.cachingStore.cacheResponse(res);
						return res;
					}).then(function(res) {
						if (store.onResponse) {
							store.onResponse(res);
						}
						return res;
					});
				}
			}

			return null;
		},

		/**
		 * If expanding a group level that does not know its child urls, then query for them
		 * @param {NodeList} nl
		 * @return {Deferred}
		 */
		_onExpandLevelIfGroup: function(nl) {
			var qr = this.getQueryResultByDomNode(nl[0]);
			var level = qr.level;

			// check that we need to query for child urls of group - this only applies to the leaf group level
			// if it was not already expanded
			if (!qr.isGroup(true) || qr.getGroupUrls() || !level.childLevel.bMain) {
				return null;
			}

			var query = qr.getQuery(true);
			var queryOptions = this.getQueryResultOptions(qr);
			var queryResults = this.getQueryResults();
			var refsProperty = qr.parentResult.store.refsProperty;
			var valueStore = qr.getRootValueStore();

			return when(valueStore.query(query, queryOptions), (results) => {
				if (valueStore.bHasRefs) {
					var parentResults = {};

					_.each(queryResults, function(qr) {
						if (qr.level == level) {
							parentResults[qr.parentResult.id] = qr.parentResult;
						}
					});

					_.each(_.values(parentResults), function(qrParent) {
						var groupStore = qrParent.store;
						var groupValue1 = qrParent.getGroupValue();

						groupStore.query(null, {useCache: true}).forEach(function(groupNode) {
							var groupValue2 = groupNode[groupStore.valueProperty];
							var item = valueStore.getByValue((groupValue1 == null) ? groupValue2 : groupValue1, groupValue2);
							groupNode[refsProperty] = (item && item[valueStore.refsProperty]) || [];
						});
					});
				} else if (!!results.length) {
					throw new QueryLimitError();
				}
			});
		},

		/**
		 * Render hierarchy icon for table row; if for a merged row, then render leaf icon (empty icon)
		 * @param {Context} ctx
		 * @param {QueryResult} qr
		 * @param {DOMNode} td
		 * @param {boolean} [bMerged] - true if rendering for merged cell
		 */
		_onFirstCell: function(ctx, qr, td, bMerged) {
			const {bHierarchy, compareData} = this;

			if (bHierarchy && !ctx.bExcel) {
				let bChildren = !bMerged && qr && qr.hasChildren(ctx);
				let row = td.parentNode;

				// if we have compare data and no children, there might be children in the compare view
				if (compareData && !bChildren && !bMerged) {
					const compareQr = compareData.getMatchingQueryResult(qr);
					if (compareQr && compareQr.nChildren) {
						// set this to true so a hierarchy icon is displayed instead of an empty icon
						bChildren = true;
					}
				}

				var hl = qr ? qr.hl : 0;
				var hs = bChildren ? csiTable.HS_PLUS : csiTable.HS_NONE;
				var span = domConstruct.create("span", {"class": csiTable.CSS_HIERARCHY_ICON}, td);
				var a = this.getAction(ACTION_HIERARCHY);
				var e = a.toHtmlWithAccess(ctx, span);
				if (row) {
					html.setDataAttribute(row, html.DATA_TABLE_STATE, hs);
				}

				this._setElementHierarchyState(e, hl, hs);
			}
		},

		_onRefreshAttribute: function(/*Context*/ctx, /*Heading*/h, /*Renderer*/ren, /*DOMNode*/refNode, /*Boolean*/bPendingChange) {
			// summary: react to attribute change
			var row = this.getRefNode(refNode);
			if (row) {
				const {queryResult} = ctx;

				// notify others
				if (refNode === this.memoryTable.getCellAtIndex(row, 0)) {
					this.onRefreshFirstCell(row, refNode);
				}

				// update cell with necessary styling to support scrolling/frozen tables
				this.scrollData.refreshRow(row);

				// update reports
				this._updateRowReport(queryResult.parentResult);
			}

			this.inherited(arguments);
		},

		_onRootDependencyPaths: function(/*String[]*/paths) {
			// summary: add any paths from matrices
			this.inherited(arguments);

			if (this.getViewNode()) {
				this.attributeSpecs.traverse(false, ({heading, isMatrix}) => {
					if (isMatrix) {
						const key = heading.getKey();
						const processor = heading.getMatrixProcessor();
						const {columnPath} = processor;

						processor.addDependencyPaths(heading, paths);

						if (columnPath) {
							this.getMatrixFilterPaths(key).forEach((path) => {
								paths.push(xml.path(columnPath, path));
							});
						}
					}
				});
			}
		},

		/**
		 * Return any query paths used by filter conditions in the given matrix key
		 * @param {string} matrixKey 
		 * @return {string[]}
		 */
		getMatrixFilterPaths(matrixKey) {
			const {filterSettings} = this;
			const filterSetting = filterSettings?.[matrixKey];
			const paths = [];

			filterSetting?.[csiPrefs.FILTER_CONDITIONS]?.forEach(({key}) => {
				const spec = this.getHeadingSpec(key);
				const path = spec?.getStandardQueryPath();
				if (path) {
					paths.push(path);
				}
			});

			return paths;
		},

		/**
		 * @override
		 * add any parameters from matrices
		 */
		_onRootRequest(request) {
			this.inherited(arguments);

			if (this.getViewNode()) {
				def.traverseHeadings(this.defView.getHeadings(), function(h) {
					if (h.matrix && !h.isHidden) {
						h.getMatrixProcessor().addRootRequestParameters(request);
					}
				});
			}
		},

		_redrawData: function() {
			// summary: redraw table body
			if (this.errorOnLoad) {
				this._endView();
			} else {
				this.tableShowWait(true);
				this.saveScrollPosition();
				this._buildCompareData()
					.then(() => this._buildBody())
					.then(() => this._postView());
			}
		},

		/**
		 * @override
		 * If view has column matrix, then redraw whole table; otherwise just redraw body
		 */
		_redrawNewData: function() {
			// FIX: currently hasMatrix does not test for just column matrix
			this.onChangeData();
			this._resetStores();

			if (this.hasMatrix()) {
				this._resetLevels();
				this._redraw();
			} else {
				this._redrawData();
			}
		},

		_redrawNode: function(/*DOMNode*/element) {
			// summary: redraw row if element in row; otherwise redraw view
			var row = this.getRefNode(element);
			if (row) {
				row = this.getRowMergeBase(row) || row;

				this._redrawNodes([row]);
			} else {
				this._redraw();
			}
		},

		/**
		 * @override
		 * Redraw given rows
		 */
		_redrawNodes(refNodes) {
			this.when(this._refreshRows(refNodes, true), () => this._endView());
		},

		/**
		 * Track first key in table
		 * @param {ICustomViewAttribute[]} attributeKeys 
		 */
		_setAttributeKeysAttr(attributeKeys) {
			this._set("attributeKeys", attributeKeys);
			this.flatKeyToInsertIfGroupFirst ||= getFirstAttributeKey(attributeKeys);
		},

		/**
		 * Query for any uncached dependency paths of rows
		 * @param {HTMLElement[]} rows
		 * @return {Deferred[]}
		 */
		_requestForMissingRowNodes(rows) {
			const urlsPerLevel = {};
			const storesPerLevel = {};
			const dfds = [];

			for (const row of rows) {
				const qr = this.getQueryResultByDomNode(row);
				const url = qr?.url;

				// TODO: figure out how to handle changes to the root row instead of just ignoring this block when qr.parentResult is null
				if (xml.isValidURL(url) && !xml.isClientUrl(url) && qr.parentResult) {
					csi.objectArrayAddValue(urlsPerLevel, qr.level.id, url);
					storesPerLevel[qr.level.id] = qr.parentResult.store;
				}
			}

			for (const [id, store] of Object.entries(storesPerLevel)) {
				const request = store.createNodesRequest(urlsPerLevel[id]);
				if (request) {
					dfds.push(this.cacheView.addRequestDeferred(request));
				}
			}

			return dfds;
		},

		/**
		 * Redraw given rows
		 * @param {HTMLElement[]} rows
		 * @param {boolean} [bQuery] - if true, then query for missing data before redraw
		 * @return {Deferred}
		 */
		_refreshRows(rows, bQuery) {
			const dfds = bQuery ? this._requestForMissingRowNodes(rows) : null;

			return this.when(all(dfds), () => {
				for (const row of rows) {
					this._refreshRow(row, true);
				}
			});
		},

		/**
		 * Optimal redraw of rows when using drag-fill
		 * @param {HTMLElement[]} rows
		 */
		refreshRowsForDragFill(rows) {
			const deleteQueue = [];
			const insertQueue = this._insertQueue = [];

			for (const row of rows) {
				// similar to code in _refreshRow/_replaceRow/insertRows, but inlined here to improve performance for multiple rows at a time
				const qr = this.getQueryResultByDomNode(row);
				const oldRows = this.getRowMerges(row);
				const newRows = this._generateResultRow(qr);
				const mainRow = newRows[0];
				const hs = this.getHierarchyState(row);
				const hsNew = this.getHierarchyState(mainRow);

				// update report row that includes row
				this._updateRowReport(qr.parentResult);

				// track rows to delete and insert - we will insert before delete so that we ensure they stay in the proper place
				deleteQueue.push(...oldRows);
				insertQueue.push([newRows, row]);

				// prepare new rows to insert
				for (const newRow of newRows) {
					this._preInsertRow(newRow, false, null, true);
				}

				if (hs == csiTable.HS_MINUS) {
					// make sure expanded state is same after replacing row
					this.setHierarchyState(mainRow, hs);
				} else if ((hsNew === csiTable.HS_NONE) && (hs != hsNew)) {
					// delete children rows (could be actions) if new row does not have children
					this._deleteChildren(row);
				}
			}

			this._flushInsertQueue();

			// delete rows that were refreshed
			for (const row of deleteQueue) {
				html.destroyElement(row);
			}

			this.publishDomChange();
		},

		/**
		 * @override
		 * Reset timestamp and redraw data
		 */
		_refreshData: function() {
			this.set("refreshTimestamp", new Date().getTime());
			this._redrawNewData();
		},

		/**
		 * Refresh given row, and update parent report rows if desired
		 * @param {DOMNode} row
		 * @param {boolean} [bUpdateReports]
		 * @return {DOMNode[]} a single row could be a matrix, so refreshing it can require creating multiple rows
		 */
		_refreshRow: function(row, bUpdateReports) {
			var bVisible = this.isRowVisible(row);
			var qr = this.getQueryResultByDomNode(row);
			var rowParent = this.getRowParent(row);
			var rowsNew = null;

			if (qr.bResultNode) {
				const bSelected = this.isSelected(row);

				rowsNew = this._generateResultRow(qr);

				if (qr.bReport) {
					// if this is a report row and is outdated, then make sure to mark refreshed rows as outdated as well
					if (qr.generateReport) {
						for (const newRow of rowsNew) {
							newRow.classList.add(CSS_REPORT_MARKER);
						}
					}
				} else if (bUpdateReports) {
					// don't do this if refreshing a report row as any changes on the report row will not affect calculations;
					// only attributes like name on a group will be changing
					this._updateRowReport(qr.parentResult);
				}

				// replace row with new row(s)
				this._replaceRow(row, rowsNew);

				if (bSelected) {
					this._selectRow(rowsNew[0], true);
				}
			} else if (qr.bAction) {
				// generate action row
				var rowNew = this.generateActionRow(qr.parentResult, qr);
				if (rowNew) {
					rowsNew = [rowNew];
					this._replaceRow(row, rowsNew);
				} else {
					this.deleteRow(row, rowParent);
				}
			}

			// hide new rows if not visible initially
			if (!bVisible) {
				_.each(rowsNew, _hide);
			}

			// Group rows being re-created during refresh, so we need to update link to the new "Newly Created" group
			if (qr.bNewlyCreated) {
				this.newlyCreatedNode = rowsNew[0];
			}

			return rowsNew;
		},

		/**
		 * Calculate reports for given query results
		 * @param {QueryResult[]} queryResults
		 * @return {Deferred} - resolves to 0 if error, 1 if processed synchronously, 2 if async
		 */
		calculateReportRows: function(queryResults) {
			const callbacks = [];
			const w = new Writer();

			w.setReport();
			w.addParameter("ReturnNodes", "Created");
			this.annotateViewWriter(w);

			for (const qr of queryResults) {
				const f = qr.getReport().generate(qr, w);
				if (f) {
					callbacks.push(f);
				} else {
					// report was not able to be generated this time, so try again next time
					qr.generateReport = true;
				}
			}

			return this.when(w.isChanged() ? w.execute() : null,
				res => {
					// cache any results and process all callbacks to complete processing
					this.cacheView.cacheResponse(res);

					for (const cb of callbacks) {
						cb();
					}

					return res ? 2 : 1;
				},
				err => {
					console.warn("Report row error: " + err.message);
					return 0;
				}
			);
		},

		/**
		 * Process outdated report rows
		 * @param {Deferred} [dfd] - if specified, then process all report rows immediately and resolve when done
		 */
		_refreshReportRows: function(dfd) {
			function resolve() {
				if (dfd) {
					dfd.resolve();
				}
			}

			if (!this.isValidView() || !this.bReporting) {
				resolve();
				return;
			}

			// filter query results to those that need to calculate results
			const queryResults = this.getQueryResults().filter(qr => {
				if (qr.generateReport) {
					qr.generateReport = false;
					return true;
				} else {
					return false;
				}
			});

			this.when(this.calculateReportRows(queryResults), result => {
				const sync = result === 1;
				const valid = result !== 0;

				// validate query results are the same as they are when we started
				// report calculations can take a long time
				// in the meantime query results can change
				const queryResultFiltered = queryResults.filter((qr) => qr === this.getQueryResult(qr.id, true));

				// render report rows again with results
				this._replaceReportRows(queryResultFiltered, valid);

				if (sync) {
					// all report rows processed and no more could exist since process was synchronous
					resolve();
				} else if (dfd) {
					// check for and process any new query results that need to calculate report results immediately
					this._refreshReportRows(dfd);
				} else {
					// schedule check for more reports later
					this.scheduleRefreshReportRows();
				}
			});
		},

		/**
		 * @override
		 * Reset view configuration and data
		 */
		_refreshWidget: function() {
			this.set("refreshTimestamp", new Date().getTime());
			this.onChangeData();
			this._resetStores();
			this._resetLevels();
			this._redraw();
		},

		/**
		 * After report nodes have been calculated, refresh the rows with the new calculations
		 * @param {QueryResult[]} queryResults
		 * @param {boolean} isSuccessful
		 */
		_replaceReportRows(queryResults, isSuccessful) {
			const rows = queryResults.map((qr) => this.getRefNodeById(qr.id));

			if (isSuccessful) {
				// regenerate report rows now that calculations are cached
				const newRows = queryResults.map((qr, i) => {
					const row = rows[i];
					if (row) {
						const newRow = this._generateReportRow(qr.getReport(), null, qr, false);
						this.setHierarchyState(newRow, this.getHierarchyState(row));
						this._preInsertRow(newRow);
						return newRow;
					}

					return null;
				});

				// fix table rows for frozen tables
				// NOTE: the order of operations is important here - make sure to batch processing of rows
				// together to get optimum performace. This is why we are using this optimized code rather than
				// the _replaceRow function which processes rows one at a time.
				this.scrollData.fixTableRows(_.compact(newRows), false);

				// replace old rows with new rows
				newRows.forEach((newRow, i) => {
					if (newRow) {
						const row = rows[i];
						row.parentNode.replaceChild(newRow, row);
						this.onInsertBodyRow(newRow);
						html.destroyElement(row);
					}
				});
			} else {
				// color rows red if error while generating report
				rows.forEach((row) => {
					row?.classList.add("attrError");
					row?.classList.remove(CSS_REPORT_MARKER);
				});
			}
		},

		_resetLevels: function() {
			// summary: reset configuration
			this.cannotReuseStoreForPrint = false;
			this.columnData = null;
			this._groupLevelSpec = null;
			this._specsByType = null;
			this._refreshPlugins = true;
			this.inherited(arguments);
		},

		_removeGroupLevels: function() {
			// summary: if no groups are found in query, then remove group levels so that table actions can still be displayed;
			// group levels will only return when view is refreshed and has results
			if (!this.bRemoveGroupsEmptyTable) {
				return;
			}
			var queryLevels = _.filter(this._queryLevels, function(ql) {return !ql.groupSpec;});
			if (queryLevels.length == this._queryLevels.length) {
				return;
			}

			var mainLevel = queryLevels[1];

			if (this.bDisableNewActionIfNoGroups) {
				mainLevel.newActions = null;
			}
			if (this.bDisableMoveActionIfNoGroups) {
				mainLevel.moveAction = null;
			}

			queryLevels[0].childLevel = mainLevel;
			this._queryLevels = queryLevels;

			var qr = this.getQueryResult();
			qr.resetStore(true);
			qr.getStore({data: []});

			this._onChangeQueryLevels(queryLevels);

			// since the query levels are changing, it does not make sense to reuse the data store when printing as
			// the print view may not remove its group levels and thus will be out of sync with this view
			this.cannotReuseStoreForPrint = true;
		},

		/**
		 * Resolve specs for main level of view
		 */
		_resolveColumns() {
			if (this.columnData) {
				return;
			}

			const {attributeSpecs} = this;
			const specs = this.columnData = new Specs();
			const ctx = this.getContext();

			this.set("columnNodeSpec", null);

			// add spec for table selection if necessary and determine which cell includes hierarchy (0/1), if any
			this._nFirstColumn = 0;

			if (this.allowsSelections()) {
				specs.add(this.getSelectionSpec());
				this._nFirstColumn = 1;
			}

			// expand attribute specs with matrix specs
			generateColumnSpecsFromAttributeSpecs(ctx, specs, attributeSpecs);

			this._resolveColumnsContinue(specs);

			// TODO: temporary code to log spec information
			// console.warn("Main Specs: ", specBuilder.defaultSourceClass.type, specBuilder.pathHintsId);

			// specs.traverse(true, (c, sc) => {
			// 	const spec = sc || c;
			// 	const space = sc ? "    " : "";
			// 	console.log(`${space}${spec.uniqueId}`);
			// });
		},

		/**
		 * Determine Spec to use as column node heading (extra row in header showing attribute for matrix column nodes)
		 * @param {Matrix} matrix 
		 */
		_resolveMatrixColumnSpec(matrix) {
			// there can only be one such heading
			if (this.columnNodeSpec || !matrix?.columnNodeHeading) {
				return;
			}

			const {columnNodeHeading, columnTargetType} = matrix;
			const {m_id, m_index, m_path} = columnNodeHeading;
			const ep = ExtendedPath.create(columnTargetType, m_path);
			const key = new Key({
				id: m_id,
				index: m_index,
				extendedPath: ep,
			});

			const specBuilder = new SpecBuilder(columnTargetType, this.getContext());
			const spec = specBuilder.getSpecForKey(key, {heading: columnNodeHeading});
			if (spec) {
				this.set("columnNodeSpec", spec);
			}
		},

		/**
		 * Complete process of generating table specs
		 * @param {Specs} cd 
		 */
		_resolveColumnsContinue(cd) {
			const {
				hasQueryResultActions,
				hiddenKeys,
				hideExtraColumn,
				hideNestedReports,
				reportSettings,
				sortSettings
			} = this;

			// iterate through specs to find column node heading
			cd.traverse(false, (spec) => {
				if (spec.isMatrix) {
					this._resolveMatrixColumnSpec(spec.heading.matrix);
				}
			});

			// track if last data column is a matrix
			const lastSpec = arrayLast(cd.cells);
			this.lastDataColumnIsGroup = lastSpec?.isMatrix || lastSpec?.isGroup;

			// if no columns, then add one by default
			if (cd.isEmpty()) {
				const spec = this.specBuilder.getSpecForKey(new Key({id: xml.ATTR_NAME}));
				if (spec) {
					cd.add(spec);
				}
			}

			// count number of columns by traversing specs
			this.numberOfColumns = this.numberOfDataColumns = cd.traverse(true, Boolean);

			// call callback once if row actions exist - send null heading to indicate it is for actions
			if (hasQueryResultActions) {
				cd.add(_getActionSpec());
				++this.numberOfColumns;
			}

			// add final column to occupy any horizontal space remaining in the table container
			if (!hideExtraColumn) {
				cd.add(_getExtraSpec());
				++this.numberOfColumns;
			}

			// finish configuration of query levels now that all column specs are known
			var groupLevels = [];
			var extraSpecs = [];
			var mainSpecs;

			this._queryLevels.forEach((ql) => {
				if (ql.groupSpec) {
					// track all group levels and specs
					groupLevels.push(ql);
					extraSpecs.push(ql.groupSpec);
				} else if (ql.bResults) {
					// calculate dependency paths for level
					const specs = this.getQueryLevelSpecs(ql);
					const paths = collectSpecDependencyPaths(specs);

					if (ql.bMain) {
						// if main level, then also include group specs as client may need to identify in which
						// group to place nodes, such as when expanding all which queries data from main store
						extraSpecs.forEach((c) => {
							def.getHeadingDependencyPaths(c.heading, paths, false, c.getStandardQueryPath());
						});
					}

					ql.addDependencyPaths(paths);

					// check for report
					if (ql._rel && ql._rel.reportType) {
						ql.report = new RollupReport(ql._rel.reportType);
					} else {
						const reports = (ql.bMain || !hideNestedReports) ? _getVisibleReportSettings(reportSettings, hiddenKeys) : [];
						ql.report = DatabaseReport.createFromSettings(reports, specs, ql.specBuilder);
					}

					if (ql.bMain) {
						mainSpecs = specs;

						// inform main query level of filterable matrix groups
						var groupSpecs = ql.groupSpecs = [];

						specs.traverse(false, function(spec) {
							if (spec.matrixExtendedPath && !spec.columnUrl) {
								groupSpecs.push(spec);
							}
						});

						// FIX: only apply DatabaseReport to group levels because GroupNodes store does not have list of all result URLs
						// which is required for RollupReport
						if (ql.report) {
							_.each(groupLevels, function(gl) {
								gl.report = ql.report;
							});
						}
					}
				}
			});

			// set store for filter widgets - this has to be done after levels are fully configured,
			// otherwise first level store is created before dependency paths are set
			// FIX - check if this is still a problem
			if (mainSpecs) {
				// add specs for sort attributes as well - they may be different than those displayed in the view
				sortSettings?.forEach((sortObj) => {
					const spec = this.getHeadingSpec(sortObj.key);
					if (spec) {
						extraSpecs.push(spec);
					}
				});

				this.onCreateQueryStore(extraSpecs);

				this.set("querySpecStore", mainSpecs.getQueryStore(true, extraSpecs));

				// initialize "query" based on user defaults
				this.setQueryDefaults();
			}
		},

		onCreateQueryStore: function(extraSpecs) {
			// summary:to add extra heading specs to toolbar filter
			return;
		},

		_setMessageAttr: function(/*String?*/text) {
			// summary: set message on left side of footer
			if (this.messageNode) {
				this.messageNode.set("text", (text == null) ? this.getQueryResultMessage() : text);
			}
		},

		_setMessageRightAttr: function(/*String?*/text) {
			// summary: set message on right side of footer
			var domNode = this.messageRightNode;
			if (domNode) {
				html.clear(domNode);
				html.text(domNode, text);
				domClass.toggle(this.footContentNode, "csi-table-foot-left-right", !!text);
			}
		},

		_setColumnNodeSpecAttr: function(/*Spec*/spec) {
			// summary: set heading to use when rendering row for nodes in column matrix
			if (spec) {
				spec.heading.m_class = (spec.heading.m_class || "") + " headingColumn";
			}

			this._set("columnNodeSpec", spec);
		},

		/**
		 * Update view compare status when comparison data is set
		 * @param {TableComparisonData} data
		 */
		_setCompareDataAttr(data) {
			const {compareData} = this;
			if (compareData) {
				compareData.compareView.destroyView();
			}

			this._set("compareData", data);
		},

		_createQueryLevel: function(/*Object*/params, /*Boolean*/bRoot) {
			// summary: configure root level correctly if showing root node
			if (bRoot && this.isShowingRootNode()) {
				params.bResults = true;
				params.css = "root";

				if (!this.relRoot) {
					params.bMain = true;
				}
			}

			return this.inherited(arguments);
		},

		_deleteChildren: function(/*DOMNode*/rowParent, /*Boolean*/bPreserveDom, /*Boolean*/bPreserveResults) {
			// summary: delete child rows of given row
			var children = this.getRowChildren(rowParent);
			if (!children.length) {
				return;
			}

			if (bPreserveResults && !bPreserveDom && !rowParent) {
				// no need for special logic to traverse body if clearing whole thing
				// TODO: can probably optimize case when displaying root row too
				html.clear(this.bodyNode, true);
				this.newlyCreatedNode = null;
			} else {
				// remove results one by one based on given parameters
				const {compareData, newlyCreatedNode} = this;
				const _recurse = (rows) => {
					_.each(rows, (row) => {
						_recurse(this.getRowChildren(row));

						if (!bPreserveResults) {
							const qr = this.getQueryResultByDomNode(row);

							this._removeQueryResult(qr);

							// if comparing, then release comparison query result so it can be matched again when children re-populated
							if (compareData) {
								compareData.releaseMatchingQueryResult(qr);
							}
						}

						if (!bPreserveDom) {
							if (row === newlyCreatedNode) {
								this.newlyCreatedNode = null;
							}

							_.each(this.getRowMerges(row), html.destroyElement, html);
						}
					});
				}

				_recurse(children);

				if (!rowParent && !bPreserveResults) {
					const qrRoot = this.getQueryResult(null, true);

					if (qrRoot) {
						// clear out root result caused by newly created group when there is two level grouping - this result is not displayed as a row
						// FIX: another way to do this would be to recurse through the query result structure to delete rather than just the DOM structure,
						// as the query result structure may have some results that are not rendered (newly created group and New action result under root result)
						const qrNewlyGenerated = qrRoot.getNewlyCreatedQueryResult(true);
						if (qrNewlyGenerated) {
							this._removeQueryResult(qrNewlyGenerated);
						}

						// ensure any data cached for reporting purposes is cleared; can't call resetChildren as it is possible
						// there is an "action" result for the toolbar
						qrRoot.resetReporting();
					}
				}
			}
		},

		_expandRowOnLoad: function(/*DOMNode*/tr, /*Number*/hl, /*Object?*/expandState) {
			// summary: return true if row id is one of given ids
			var b = expandState?.[this.getRefNodeId(tr)];
			if (b) {
				return true;
			} else if (b == null) {
				return this.getHierarchyLevel(tr) < hl;
			}

			return false;
		},

		_generateChildren: function(/*DOMNode*/row, /*DOMNode*/rowNext) {
			// summary: main call to populate table rows
			const qr = this.getQueryResultByDomNode(row);
			let queryOptions;
			let store;

			// if view is suspended, then ignore all query requests, but set refreshOnSuspend to true to indicate
			// view query has changed since suspend mode is turned off
			if (this.isSuspended()) {
				this.set("refreshOnSuspend", true);

				if (this.showCountIfSuspended) {
					store = this.get("store");

					// check for a null store just in case; if null, then just ignore this setting (showCountIfSuspended)
					if (store) {
						queryOptions = {
							requestParameters: {
								"Operation": store.requestOperationCount,
								"Qry.Limit.Filter": constants.QUERY_COUNT_LIMIT
							}
						};
					} else {
						return null;
					}
				} else {
					return null;
				}
			}

			// set loading message if at root level of table
			if (qr.isRoot()) {
				this.set("message", locale.resource("LoadingEllipsis"));
			}

			// change tree icon to expanded
			this.setHierarchyState(row, csiTable.HS_MINUS);

			// execute query to expand current row
			var dfd = this._query(qr, queryOptions, store);
			var dfds = [dfd];

			return this.when(all(dfds),
				results => {
					// results[0] is resolved value of dfd above
					const resultNodes = results[0];
					this.onViewResults(qr, resultNodes);
					return this._buildViewResults(qr, row, rowNext, resultNodes, true);
				},
				err => {
					return this._buildViewResults(qr, row, rowNext);
				}
			);
		},

		generateActionRow: function(/*QueryResult*/qrParent, /*QueryResult?*/qr) {
			// summary: render row for actions available for parent result
			if (state.get("print")) {
				return null;
			}

			var ctx = qrParent.getChildContext();
			var bMore = this.moreAction && qrParent.hasMoreChildren();

			// determine if more action should be shown based on current display count and target if at root level
			if (bMore && qrParent.isRoot()) {
				const count = this.queryDisplayCount;
				const target = this.queryDisplayCountTarget;

				if (target && (count < target)) {
					bMore = false;
				}
			}

			// show actions in table if can have multiple levels with new action on child level
			var bNewActions = this.bForceNewActionsInTable;

			if (!bNewActions && this.hasResultRows()) {
				// otherwise, row new actions only available for ordered not grouped tables and ordered grouped by section
				bNewActions = qrParent.level.bAllowNewActionsWhenGrouped || (qrParent.hasOrderedChildren() && (!this.hasGroups() || this.isGroupBySection()));
			}

			// get actions for row
			var actions = bNewActions ? qrParent.getNewActions(ctx, bMore) : [];

			if (bMore) {
				actions.unshift(this.showAllAction);
				actions.unshift(this.moreAction);
			}

			if (!actions.length) {
				return null;
			}

			// create action row - use parent context to evaluate
			const moreOnly = bMore && (actions.length == 2);
			const classes = [html.CSS_NO_EXPORT, moreOnly ? "csi-table-row-more" : "csi-table-row-actions"];
			var ql = qrParent.level.childLevel;

			if (ql.css) {
				classes.push(ql.css);
			}

			if (ql.rules) {
				def.processRulesCSS(ctx, ql.rules, classes);
			}

			var tr = this._generateTableRow(classes);
			var td = this.generateCell(null, tr);
			td.colSpan = this.numberOfDataColumns;

			if (this.lastDataColumnIsGroup) {
				$(td).addClass(CSS_MATRIX_COLUMN_LAST);
			}

			qr = this.setQueryResultActionContext(qrParent, ctx, tr, qr);

			// add hierarchy margin for first data column for new actions
			if (!moreOnly) {
				this._onFirstCell(ctx, qr, td);
			}

			// generate action Combo Button with context for drop-down menu to be identifiable for automation
			var defAction = actions.shift();
			var cssClass = "csi-secondary-cmb-btn csi-actions-new" + (this.bHierarchy ? "" : " csi-no-tableRowWidget") + (moreOnly ? " csi-rounded-combo-btn" : "");
			if (this.scrollData.useNaturalHorizontalScrolling()) {
				cssClass += " csi-table-natural-scroll-more";
			}

			var key = this.defView.getHtmlKey();
			const actionName = locale.resource(defAction.getName());
			new ActionsComboButton(Automation.params(key, "actionRow", qrParent.getAutomationId(), {
				"class": cssClass,
				materialIcon: moreOnly ? "" : 'add',
				showLabel: moreOnly,
				label: moreOnly ? actionName : "",
				title: actionName,

				actions: actions,
				onAction: lang.hitch(this, "_onElementAction", td),
				onClick: lang.hitch(this, "_onElementActionClick", td, defAction),
				onNext: lang.hitch(this, "_onElemenActionNext", td)
			})).placeAt(td);

			if (this.get("hasQueryResultActions")) {
				td = this.generateCell(_getActionSpec(), tr);
			}

			if (!this.hideExtraColumn) {
				td = this.generateCell(_getExtraSpec(), tr);
			}

			return tr;
		},

		/**
		 * Create report row
		 * @param {QueryReport} report - report configuration
		 * @param {QueryResult} [qrParent] - query result for node containing results to be reported
		 * @param {QueryResult} [qr] - query result representing report row; could be same as above
		 * @param {boolean} [needToGenerate] - set to true if report results are not yet generated and should be marked to generate
		 * @return {DOMNode}
		 */
		_generateReportRow: function(report, qrParent, qr, needToGenerate) {
			const {compareData} = this;

			this.bReporting = true;

			if (!qrParent) {
				qrParent = qr.bResultNode ? qr : qr.parentResult;
			}

			// allocate new QueryResult
			qr = qr || this._createQueryResult({
				bReport: true,
				hl: qrParent.hl + 1,
				level: qrParent.level.childLevel,
				parentResult: qrParent,
				parentUrl: qrParent.url,
				url: xml.getClientNodeUrl(REPORT_URL_SUFFIX)
			});

			// determine if number of results for report row exceeds limit
			var headingCount = this.getHeadingCount();
			var limit = this.getQueryLimitIfReached(qrParent);

			// create report row
			var specs = this.getQueryLevelSpecs(qr.level);
			var ctx = qr.getContext();
			var css = [CSS_REPORT];

			if (!this.hasResultRows()) {
				css.push("nodisplay");
			}

			// when exporting to excel or comparing, report row calculations are already done, so no need to regenerate
			if (ctx.bExcel || qr.bCompareNode) {
				needToGenerate = false;
			}

			if (needToGenerate && !limit) {
				css.push(CSS_REPORT_MARKER);
				qr.generateReport = true;
			}

			if (qr.bResultNode) {
				css.push(qr.level.css);
			}

			var tr = this._generateTableRow(css);
			var reportLabelColumn = this._nFirstColumn;

			ctx.bReporting = true;

			if (!ctx.bExcel) {
				ctx.applyNodeContext(tr);
				this.setRefNodeId(tr, qr.id);
				this.setHierarchyLevel(tr, qr.hl);
			}

			specs.traverse(true, function(c, sc, i, n) {
				// if report cannot be generated because results exceed limit, then ignore all headings other than first
				if (limit && (n > reportLabelColumn) && (n < headingCount)) {
					return;
				}

				// create cell
				var bLabelColumn = (n == reportLabelColumn);
				var td = this.generateCell(null, tr);
				var bRender;

				sc = sc || c;

				if (sc.css) {
					domClass.add(td, sc.css);
				}

				if (bLabelColumn || (!needToGenerate && report.contains(sc))) {
					bRender = true;

					var mp = c.heading.getMatrixProcessor();
					if (mp) {
						mp.render(ctx, qr, sc, n, td);
					} else if (bLabelColumn) {
						this._onFirstCell(ctx, qr, td);
						csiHeading.toHtml(ctx, (qr.groupValue == null) ? _nameHeading : this._getGroupLevelSpec().heading, td);
					} else {
						csiHeading.toHtml(ctx, sc.heading, td);
					}
				} else if (n > reportLabelColumn) {
					// render row actions in actions column, otherwise leave empty
					if (sc.isActionsColumn() && !qr.bCompareNode) {
						qr.renderActions(ctx, td, lang.hitch(this, "_onActionsMenuClick", tr));
					}

					sc.applyCss(td);
					return;
				}

				// first column should be labeled as Report if no other value
				if (bLabelColumn && !td.innerText.trim()) {
					if (bRender) {
						csiHeading.clearHtml(td);
					}

					html.create("span", {innerText: locale.resource("Report")}, td);
				}

				if (bLabelColumn && limit) {
					// if frozen columns, then first cell should span whole table and show message indicating it is disabled
					td.colSpan = headingCount;

					html.create("span", {
						"class": "tableViewReportDisabled",
						innerText: locale.resource("ReportLimitReached", [limit])
					}, td);
				}

				if (!i && qr.bNewlyCreated) {
					this._generateRefreshShortcut(td.lastChild || td);
				}
			}, this);

			// report row does not apply CSS from specs, so make sure to apply here for actions column so cell is not exported
			if (this.get("hasQueryResultActions")) {
				domClass.add(tr.lastChild, html.CSS_NO_EXPORT);
			}

			// add comparison information
			if (compareData && !needToGenerate) {
				compareData.compareAndAugmentRow(qr, tr);
			}

			return tr;
		},

		/**
		 * Render heading content to cell
		 * @param {QueryResult} qr
		 * @param {Context} ctx
		 * @param {Spec} spec
		 * @param {DOMNode} tr - table row
		 * @param {DOMNode} td - table cell where to render content; if not specified, then assumption is that this is cell to merge with original
		 * @param {Boolean} bFirstCell
		 * @return {DOMNode} - returns the rendered cell (td)
		 */
		_generateResultCell: function(ctx, qr, spec, tr, td, nColumn, bFirstCell) {
			var bMerged = false;
			var imageWrapper;

			if (!td) {
				// if not specific, then generate a new cell and add to cells to be merged with original
				td = this.generateCell(spec);

				if (spec.css) {
					domClass.add(td, spec.css);
				}

				ctx.cellMerge = ctx.cellMerge || [];
				ctx.cellMerge[nColumn] = ctx.cellMerge[nColumn] || [];
				ctx.cellMerge[nColumn].push(td);
				bMerged = true;
			}

			if (bFirstCell) {
				this._onFirstCell(ctx, qr, td, bMerged);
			}

			// add column dimensions to context in case needed for rendering content (i.e. images)
			if (!ctx.bPrint) {
				ctx.availableWidth = this.scrollData.getAvailableColumnWidth(nColumn);
				ctx.availableHeight = this._getTableImageHeight();
			}

			csiHeading.toHtml(ctx, spec.heading, td);

			if (imageWrapper && domClass.contains(imageWrapper, csiHeading.CSS_EDITABLE)) {
				csiHeading.addHeadingNodeEditableStyling(imageWrapper.parentNode);
			}

			return td;
		},

		_generateResultRow: function(/*QueryResult*/qr, /*DOMNode[]?*/rows, /*Context?*/ctx) {
			// summary: generate rows for given nodes
			const {compareData} = this;

			rows = rows || [];

			if (qr.bReport) {
				// forward group rows that also serve as report rows to function to generate report row;
				rows.push(this._generateReportRow(qr.getReport(), null, qr, true));
				return rows;
			}

			const bCreateContext = !ctx;

			if (bCreateContext) {
				ctx = qr.getContext();
				ctx.preRollup();
			}

			qr.preProcessContext(ctx);
			this.onGenerateResultRow(ctx);

			// determine CSS for row
			const classes = [];
			const ql = qr.level;
			const type = ctx.getNodeType();
			const newRows = [];

			if (ql.css) {
				classes.push(ql.css);
			}

			if (ql.rules) {
				def.processRulesCSS(ctx, ql.rules, classes);
			}

			// create row
			const tr = this._generateTableRow(classes);
			newRows.push(tr);

			// excel export does not need any attributes on row
			if (!ctx.bExcel) {
				this.setRefNodeId(tr, qr.id);
				this.setHierarchyLevel(tr, qr.hl);
				ctx.applyNodeContext(tr);

				if (qr.bResultNode) {
					csiTable.setRowAsResult(tr);
				}
			}

			// hard code matrix images to thumbnails unless already set to icon for view or if matrix supports different image sizes
			var imageSize = ctx.prefImageSize;
			var imageSizeMatrix = (imageSize == reflection.IMAGE_SIZE_ICON || this.bPrefImageSizeInMatrix) ? imageSize : reflection.IMAGE_SIZE_SMALLEST;

			// render row with all columns
			var specs = this.getQueryLevelSpecs(ql, type);
			var nColumn = 0;

			specs.traverse(false, (c, sc, i, n) => {
				// no need to render actions column when exporting to excel
				if (ctx.bExcel && c.isActionsColumn()) {
					return;
				}

				const {heading, isGroup, isMatrix, subs} = c;
				let bFirstCell = (n == this._nFirstColumn);
				let td = this.generateCell(c, tr);

				if (c.isActionsColumn()) {
					// render row actions; do not create row actions for "Newly Created" group row
					if (!qr.bNewlyCreated && !qr.bCompareNode) {
						qr.renderActions(ctx, td, lang.hitch(this, "_onActionsMenuClick", tr));
					}
				} else if (isMatrix) {
					// render matrix rows and columns
					ctx.prefImageSize = imageSizeMatrix;

					var mp = heading.getMatrixProcessor();
					if (mp) {
						// means MatrixClass is defined for the heading.
						nColumn = mp.render(ctx, qr, c, nColumn, td);
					} else {
						// traverse dynamic columns
						nColumn = this._generateRowColumns(ctx, c, nColumn, td, true);
					}

					ctx.prefImageSize = imageSize;
				} else {
					const cells = isGroup ? subs : [c];

					cells.forEach((spec, j) => {
						if (j > 0) {
							if (c.css) {
								domClass.add(td, c.css);
							}

							td = this.generateCell(c, tr);
							bFirstCell = false;
						}

						// render non-matrix cell
						this._generateResultCell(ctx, qr, spec, tr, td, nColumn, bFirstCell);

						// create "Refresh" shortcut for "Newly Created" row
						if (bFirstCell && qr.bNewlyCreated) {
							this._generateRefreshShortcut(td.lastChild || td);
						}

						++nColumn;
					});
				}

				// apply CSS after data - otherwise headingToHtml will wipe it out
				if (c.css) {
					domClass.add(td, c.css);
				}
			}, this);

			// if merge cells created for this row, then add now
			this._generateMergedRows(ctx, qr.hl, tr, newRows);

			qr.postProcessContext(ctx);

			if (bCreateContext) {
				ctx.postRollup();
			}

			// add comparison information
			if (compareData && !ctx.bExcel) {
				for (const row of newRows) {
					compareData.compareAndAugmentRow(qr, row);
				}
			}

			rows.push(...newRows);

			return rows;
		},

		/**
		 * Create "Refresh" shortcut for NewlyCreated row
		 * @param {DOMNode} td
		 */
		_generateRefreshShortcut: function(td) {
			domClass.add(td, "csi-table-newly-created");
			var a = this.getAction(ACTION_NEW_REFRESH);
			td.innerText += " ";
			a.toHtml(this.getContext(), td);
		},

		/**
		 * Create "Newly Created" group row; for multilevel grouping, only render last level
		 * @param {Node[]} [newNodes] - used in inherited classes when overriding this method
		 * @param {boolean} [bBlanks] - if true, then generate "Blanks" group if not already existing
		 * @return {DOMNode}
		 */
		generateNewlyCreatedRow: function(newNodes, bBlanks) {
			let tr = this.newlyCreatedNode;
			if (tr) {
				return tr;
			}

			const {cacheView} = this;
			const groupText = locale.resource(bBlanks ? "Blanks" : "NewlyCreatedGroup");
			const groupValue = bBlanks ? xml.VALUE_BLANK : xml.VALUE_NEWLY_CREATED;
			const qrRoot = this.getQueryResult();
			const qrNext = qrRoot.getFirstChildResult(true);

			// for "Blanks" we don't mark as newly created so that we don't remember link to newly created row nor create "refresh" shortcut
			const bNewlyCreated = !bBlanks;

			// keep hierarchy level at first level so indentation is right
			const hl = qrRoot.hl + 1;

			const createGroup = (qrParent, qrNext) => {
				const group = GroupNodes.createClientNode(null, "", null, groupText, groupValue);

				cacheView.put(group);

				const qr = this._createQueryResult({
					bNewlyCreated,
					bReport: !!qrParent.getReport(),
					bResultNode: true,
					hl,
					level: qrParent.level.childLevel,
					parentResult: qrParent,
					parentUrl: qrParent.url,
					url: group.$URL
				}, qrNext);

				// construct store for this group as it will be expanded
				qr.getStore();

				return qr;
			};

			// create new group before first existing group
			let qrGroup = createGroup(qrRoot, qrNext);

			// if two levels of groups, then create another
			if (qrGroup.containsGroups()) {
				this._insertQueryResult(qrGroup, true);
				qrGroup = createGroup(qrGroup);
			}

			const ctx = qrGroup.getChildContext();
			const rows = [];

			// generate row and expand
			this._generateResultRow(qrGroup, rows, ctx);
			tr = rows[0];

			if (bBlanks) {
				// if we are generating "Blanks" row , then add action row
				const actRow = this.generateActionRow(qrGroup);
				if (actRow) {
					rows.push(actRow);
				}
			} else {
				// remember link to the pure "Newly Created" row
				this.newlyCreatedNode = tr;
			}

			// insert newly created row to the table
			const rowNext = qrNext ? this.getRefNodeById(qrNext.id) : null;
			this.insertRows(rows, null, rowNext, true);

			return tr;
		},

		/**
		 * Create and return table row with given classes
		 * @param {string[]} [classes]
		 * @return {DOMNode}
		 */
		_generateTableRow: function(classes) {
			var css = CSS_ROW;

			if (classes) {
				css += " " + classes.join(" ");
			}

			return domConstruct.create("tr", {
				"class": css
			});
		},

		_onActionsMenuClick: function(/*DOMNode*/tr, /*DOMNode*/actionNode) {
			try {
				if (this._selectionSimulation() && tr) {
					this._selectRow(tr, true);
				}
				// only return true is Action exists and invoked
				return csiAction.invoke(this, actionNode, null, {
					bClick: true,
					refNode: tr
				});
			} catch (ex) {
				console.warn(ex);
			}
		},

		_generateMergedRows: function(/*Context*/ctx, /*Number*/hl, /*DOMNode*/trBase, /*DOMNode[]*/rows) {
			// summary: create rows for extra cells per column
			const {cellMerge} = ctx;

			if (!cellMerge) {
				return;
			}

			ctx.cellMerge = null;

			// calculate number of rows needed to show extra cells
			var nMax = _.reduce(cellMerge, function(n, tds) {
				return tds ? Math.max(n, tds.length) : n;
			}, 0);

			if (!nMax) {
				return;
			}

			// create additional rows to merge at hierarchy level one above current so client sorting works
			const {isComparing} = this;
			var mergedRows = [];
			var id = this.getRefNodeId(trBase);
			var css = trBase.className;
			var tr;

			for (var i = 0; i < nMax; ++i) {
				tr = domConstruct.create("tr");
				tr.className = css;

				if (!ctx.bExcel) {
					html.setDataAttribute(tr, html.DATA_TABLE_MERGED, true);
					this.setRefNodeId(tr, id);
					this.setHierarchyLevel(tr, hl);
					ctx.applyNodeContext(tr);
				}

				mergedRows.push(tr);
				rows.push(tr);
			}

			this.columnData.traverse(true, function(c, sc, i, n) {
				var td0 = trBase.childNodes[n];
				var tds = cellMerge[n];
				var nCells = tds && tds.length;
				var spec = sc || c;
				var j;
				var td;

				if (nCells) {
					// add extra cells of matrix
					for (j = 0; j < nCells; ++j) {
						mergedRows[j].appendChild(tds[j]);
					}

					// add empty cells to matrix if one has more rows than another
					for (j = nCells; j < nMax; ++j) {
						td = domConstruct.create("td", {
							className: "csi-bg-not-applicable"
						});

						if (isComparing) {
							html.setDataAttribute(td, html.TABLE_CELL_COMPARE_KEY, "");
						}

						spec.applyCss(td);
						mergedRows[j].appendChild(td);
					}
				} else if (td0) {
					// td0 can be null in excel export case when Actions column is omitted
					td0.rowSpan = nMax + 1;
				}
			});
		},

		_onElemenActionNext: function(/*DOMNode*/td, /*Number*/key, /*Boolean*/bShift) {
			// summary: invoke action for cell
			var refNode = this.findNextHeadingNode(td, key, bShift);
			if (refNode) {
				this.editHeading(refNode, false, {bInlineEdit: true});
			}
		},

		_onChangeRowChildren: function(/*DOMNode*/rowParent) {
			// summary: update list of children urls for row
			if (this.isGroupRow(rowParent)) {
				rowParent = this.refreshParentRows(rowParent, true);
			}

			// update report if any
			this._updateRowReport(this.getQueryResultByDomNode(rowParent));

			// DOM has changed so notify others
			this.publishDomChange();

			return rowParent;
		},

		/**
		 * @override
		 * Resize/freeze table and render placeholder if empty
		 */
		_onResize() {
			if (this.bViewed && !state.get("print")) {
				this._scroll();
				this.queryMoreIfNeeded();
				this.updatePlaceholder();
			}
		},

		getAutoExpandLevel: function() {
			// summary: return number of levels to auto-expand with configured expand level
			var hl = this.getPageLevel();
			var qr = this.getQueryResult();
			var l = qr.level.childLevel;

			while (l && l.bAutoExpand) {
				++hl;

				if (l == l.childLevel) {
					break;
				} else {
					l = l.childLevel;
				}
			}

			// add in expand level for additional levels to expand unless group level was not set to auto-expand
			if (l && !l.groupSpec) {
				hl += (this.expandLevel - 1);
			}

			return hl;
		},

		_autoExpand: function() {
			// summary: auto-expand table based on settings
			const {compareData, expandState, isComparing} = this;
			const hl = (isComparing && !compareData) ? Infinity : this.getAutoExpandLevel();

			this.set("expandState", null);

			// expand specific nodes in tree
			return this._expand(null, tr => this._expandRowOnLoad(tr, hl, expandState));
		},

		/**
		 * @override
		 * Auto-expand table to desired level and wrap up loading process
		 */
		_postViewDeferred: function(ctx, dfds) {
			this.inherited(arguments);

			// if widget is currently hidden, then need to recalculate row heights on next resize
			this._resizeRowsOnNextResize = !this.headNode.offsetHeight;

			dfds.push(this._autoExpand()
				.then(() => {
					// update footer message when done loading
					this.set("message");

					if (ctx.bPrint || this.isComparing) {
						// when printing, process report rows if table includes reports as they need to finish loading before proceeding;
						// when not printing, report row processing can be delayed (_endView)
						const dfd = new Deferred();
						this._refreshReportRows(dfd);
						return dfd;
					} else {
						this.updatePlaceholder();
						this.restoreScrollPosition().then(() => this.scrollData.updateBodyScroll());
					}
				})
			);
		},

		_resolveView: function() {
			// summary: resolve view
			var defView = this.defView;

			defView._addViewAction(ACTION_COLUMN_RESIZE, {
				bKeepViewEnabled: true,
				m_onAction: "onActionSetValue"
			});
			defView._addViewAction(ACTION_HIERARCHY, {m_typeControl: def.ACTION_ICON});
			defView._addViewAction(ACTION_HEADER);
			defView._addViewAction(ACTION_NEW_REFRESH, {
				m_postAction: "refreshDataPreserveHierarchy",
				m_name: locale.resource("Refresh"),
				m_typeControl: def.ACTION_LINK
			});
			defView._addViewAction(ACTION_RESET_COLUMN_WIDTHS, {
				bKeepViewEnabled: true,
				m_onAction: "onActionSetValue",
				m_postAction: "redrawSpecs"
			});
			defView._addViewAction(ACTION_SELECT_ALL, {
				bKeepViewEnabled: true,
				m_postAction: (ac) => this.when(this.queryMoreToTarget(true), () => this.toggleAllSelections(ac.actionValue))
			});

			// enable the checkbox as configured
			if (this.enableMassSelect) {
				const controller = new Controller({
					m_action: new Action(),
					m_element: this.domNode,
					widget: this
				});

				const fd = lang.mixin(new FormData(controller), {
					m_bEditable: true,
					m_bSelectOne: false,
					m_bRequireOne: false,
					m_bAllowSelections: true
				});

				defView.setFormData(fd);
			}

			this.inherited(arguments);

			if (!this.relRoot) {
				// remove toolbar plugins if no relationship
				this.plugins = _.without(this.plugins, "charts", "filter", "pageSize", "tableExcelFlat", "tableSuspend");
			} else if (!this.defView.isSlim(true)) {
				// table can only be split if it has relationship and is not defined as slim
				this.set("splitterType", "table");
			}

			// determine if new actions should be forced in table, which will be true if the table can have more than one
			// level (even when ungrouped), and the lower level can have a new action
			var rels = defView.m_relationships;
			var relLength = rels ? rels.length : 0;
			var rel;

			if (relLength > 1) {
				rel = rels[1];
			} else if (relLength && rels[0].m_bRepeat) {
				rel = rels[0];
			}

			if (rel && rel.m_news) {
				this.bForceNewActionsInTable = true;
			}
		},

		/**
		 * Setup table to scroll vertically and horizontally based on view settings. If freezeHeader specified,
		 * then that column will be the base for horizontal scrolling (or turned off if column already the base)
		 */
		_scroll: function() {
			if (this.isValidView() && !this.isLoadingView() && !!this.domNode.offsetHeight && this._shouldResize()) {
				// if (state.get("debugMode")) {
				// 	console.time("table resize");
				// }

				this.scrollData.resize();

				if (this._resizeRowsOnNextResize) {
					this.scrollData.refreshRows();
					this._resizeRowsOnNextResize = false;
				}

				// if (state.get("debugMode")) {
				// 	console.timeEnd("table resize");
				// }
			}
		},

		/**
		 * Show/hide wait layer with delay if applicable
		 * @param {boolean} bShow - show if true, hide if false
		 */
		tableShowWait: function(bShow) {
			const ctx = this.getContext();
			// dont show wait layer when printing and when in composite view (like TDS)
			if (!ctx.bPrint && !this.viewParent) {
				html.showWait(bShow, false, true);
			}
		},

		/**
		 * Mark result and parent results with report rows as outdated; if group, then it is same as report row, otherwise look to first child
		 * @param {QueryResult} qr
		 */
		_updateRowReport: function(qr) {
			if (!qr || !this.bReporting) {
				return;
			}

			// be careful to capture parentResult here before possibly switching to child result
			const {bReport, parentResult} = qr;

			// check to see if first child is a report row (a sibling report)
			if (!bReport) {
				qr = qr.getFirstChildResult();
			}

			if (qr?.bReport) {
				// it is possible that row does not exist for newly created group when 2 levels of grouping
				const row = this.getRefNodeById(qr.id);
				if (row) {
					row.classList.add(CSS_REPORT_MARKER);

					if (!qr.generateReport) {
						qr.generateReport = true;
						this.scheduleRefreshReportRows();
					}
				}

				// update any parent report rows
				this._updateRowReport(parentResult);
			}
		},

		// @PostAction functions

		postActionRefreshColumns: function(/*Controller*/ac) {
			// summary: redraw after recalculating specs
			return this.redrawSpecs();
		},

		postActionRefreshRows: function(/*Controller*/ac) {
			return this.redrawData();
		},

		postActionRefreshModifiedRows: function(/*Controller*/ac) {
			// summary: refresh modified data rows
			const urls = ac.getModifiedUrls();
			if (urls) {
				const obj = csi.hashArray(urls, null, Boolean);

				return this.refreshDataRows((tr) => {
					return obj[this.getRefNodeUrl(tr)];
				}, true);
			}
		},

		postActionRefreshModifiedRowsWithDefault: function(/*Controller*/ac) {
			// Post action function refreshes the row that was copied, wrapper causes default copy postaction to still fire and update new row
			this.postActionRefreshModifiedRows(ac);
		},

		postActionRefreshParentRows: function(/*Controller*/ac) {
			// summary: refresh modified data rows
			this.refreshParentRows(this.getRefNode(ac.m_element));
		},

		postActionRefreshHierarchyRow: function(/*Controller*/ac) {
			// summary: expand/collapse row based on changes to children - force expand if not editing an attribute (for example, if row action to select children)
			return this.refreshHierarchyRow(this.getRefNode(ac.refNode || ac.m_element), !ac.heading) || new Deferred().resolve();
		},

		/**
		 * Same as "refreshData" but preserve hierarchy
		 * @return {deferred}
		 */
		refreshDataPreserveHierarchy: function() {
			this.captureHierarchyState();
			return this.refreshData();
		},

		/**
		 * Redraw widget if "Newly Created" group is present
		 * @return {Deferred} - return deferred or "false"
		 */
		refreshDataIfNewlyCreated: function() {
			var qrRoot = this.getQueryResult();
			return qrRoot && qrRoot.getNewlyCreatedQueryResult(true) && this.refreshDataPreserveHierarchy();
		},

		/**
		 * @override
		 * Return dropdown options for table custom view
		 */
		getCustomViewDropDownOptions() {
			// Freeze columns dropdown options
			const freezeColumns = [];
			const startIndex = api.allowNonFrozenTables() ? 0 : 1;

			for (let i = startIndex; i <= constants.TABLE_MAX_FREEZE_COLUMNS; ++i) {
				freezeColumns.push({id: String(i), value: i});
			}

			// Image size dropdown options
			const imageSizes = convertEnumToComponentItem(reflection.IMAGE_SIZE_ENUM);
			imageSizes.push({id: IMAGE_SIZE_CUSTOM, res: "Custom"});

			// Result count dropdown options
			const resultCounts = this.get("querySizeStoreForCustomView");

			// Height dropdown options
			const full = this.get("allowFullHeight");
			const tableHeights = [
				{id: constants.TABLE_HEIGHT_SMALL, res: "ViewPreferences_HeightSmall"},
				{id: constants.TABLE_HEIGHT_MEDIUM, res: "ViewPreferences_HeightMedium"},
				{id: constants.TABLE_HEIGHT_LARGE, res: "ViewPreferences_HeightLarge"}
			];

			if (full) {
				tableHeights.unshift({id: constants.TABLE_HEIGHT_FULL, res: "ViewPreferences_TableHeightFull"});
			}

			// Chart height dropdown options
			const chartHeights = [
				{id: constants.CHART_HEIGHT_SMALL, res: "ViewPreferences_HeightSmall"},
				{id: constants.CHART_HEIGHT_MEDIUM, res: "ViewPreferences_HeightMedium"},
				{id: constants.CHART_HEIGHT_LARGE, res: "ViewPreferences_HeightLarge"}
			];

			// Expand level dropdown options
			const expandLevels = [
				{id: "1", name: "1", value: 1},
				{id: "2", name: "2", value: 2},
				{id: "3", name: "3", value: 3},
				{id: "4", name: "4", value: 4},
				{id: "5", name: "5", value: 5}
			];

			// Paper size and paper orientation dropdown options
			const options = this.inherited(arguments);

			return {
				...options,
				chartHeights,
				expandLevels,
				freezeColumns,
				imageSizes,
				resultCountsIndex: resultCounts.index,
				resultCounts: resultCounts.data,
				tableHeights
			};
		},
	});
});
